import React, {useState, useEffect, useRef} from 'react';
import {useAPI} from '../Contexts/APIContext';
import {useAuth} from '../Contexts/AuthContext';
import {
  initializeAllowlist,
  isUserAllowed,
  getAllowlist,
  addToAllowlist,
  removeFromAllowlist,
  setAPIContext
} from '../utils/allowlistManager';
import styles from './CodeManager.module.css';

function CodeManager() {
  // Authentication check
  const { currentAuthUser, isCurrenAuthUserLoading, getCurrentUser } = useAuth();
  const [authError, setAuthError] = useState(null);
  const [showAllCodes, setShowAllCodes] = useState(false);
  
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingTable, setEditingTable] = useState(''); // 'active' or 'disabled'
  const [originalEditData, setOriginalEditData] = useState(null); // Store original data when editing
  const [inviteCodes, setInviteCodes] = useState([]);
  const [activeCodes, setActiveCodes] = useState([]);
  const [disabledCodes, setDisabledCodes] = useState([]);
  const [deletedCodes, setDeletedCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerIndex, setDatePickerIndex] = useState(-1);
  const [datePickerTable, setDatePickerTable] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [showNewCodeModal, setShowNewCodeModal] = useState(false);
  const [newCodeData, setNewCodeData] = useState({
    inviteCode: '',
    inviteLimit: '',
    expirationDate: '',
    geoRestriction: 'worldwide', // 'worldwide' or 'selected_countries'
    allowMultipleSignup: true,
    referralGroupObject: 'artist',
    feelsId: ''
  });
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [suggestedCode, setSuggestedCode] = useState('');
  const [canLoadMoreActive, setCanLoadMoreActive] = useState(false);
  const [canLoadMoreDisabled, setCanLoadMoreDisabled] = useState(false);
  const [activeCurrentBatch, setActiveCurrentBatch] = useState(1);
  const [disabledCurrentBatch, setDisabledCurrentBatch] = useState(1);
  const [showInviteLimitModal, setShowInviteLimitModal] = useState(false);
  const [inviteLimitModalData, setInviteLimitModalData] = useState({
    index: -1,
    table: '',
    currentLimit: '',
    newLimit: ''
  });
  const [showQuickExpireModal, setShowQuickExpireModal] = useState(false);
  const [quickExpireModalData, setQuickExpireModalData] = useState({
    index: -1,
    table: '',
    inviteCode: ''
  });
  const [showExtLinkTooltip, setShowExtLinkTooltip] = useState(false);
  const [extLinkTooltipTimer, setExtLinkTooltipTimer] = useState(null);
  const [showManageAccessModal, setShowManageAccessModal] = useState(false);
  const [allowlistUsers, setAllowlistUsers] = useState([]);
  const [newUserAlias, setNewUserAlias] = useState('');
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [selectedCodesForDelete, setSelectedCodesForDelete] = useState(new Set()); // Now stores invite codes instead of indices
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [modalError, setModalError] = useState('');
  const [isSavingSong, setIsSavingSong] = useState(false);
  const [showGeoRestrictionModal, setShowGeoRestrictionModal] = useState(false);
  const [geoRestrictionModalData, setGeoRestrictionModalData] = useState({
    index: -1,
    table: '',
    currentGeoRestriction: 'worldwide',
    newGeoRestriction: 'worldwide'
  });
  
  // Table refs for smooth scrolling
  const activeTableRef = useRef(null);
  const disabledTableRef = useRef(null);
  const deletedTableRef = useRef(null);
  
  // Pagination state
  const [activeCurrentPage, setActiveCurrentPage] = useState(1);
  const [disabledCurrentPage, setDisabledCurrentPage] = useState(1);
  const [activeTotalPages, setActiveTotalPages] = useState(1);
  const [disabledTotalPages, setDisabledTotalPages] = useState(1);
  const [activeCodesTotal, setActiveCodesTotal] = useState(0);
  const [disabledCodesTotal, setDisabledCodesTotal] = useState(0);
  const [loadingActive, setLoadingActive] = useState(false);
  const [loadingDisabled, setLoadingDisabled] = useState(false);
  const [deletedCurrentPage, setDeletedCurrentPage] = useState(1);
  const [deletedTotalPages, setDeletedTotalPages] = useState(1);
  const [deletedCodesTotal, setDeletedCodesTotal] = useState(0);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false); // Track background loading
  const ITEMS_PER_PAGE = 20;

  const {
    fetchAllInviteCodes,
    getAllInviteCodes,
    tryAlternativeInviteEndpoints,
    createInviteCode,
    updateInviteCode,
    deleteInviteCode,
  } = useAPI();


  // Set up API context for allowlist manager
  const apiContext = useAPI();
  useEffect(() => {
    setAPIContext(apiContext);
  }, [apiContext]);

  // Enhanced loader to support up to 1000 active + 1000 inactive codes
  const loadInviteCodesSimple = async () => {
    setLoading(true);
    setError(null);

    try {
      let allCodes = [];
      let activeCodes = [];
      let disabledCodes = [];
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Load timeout after 30 seconds')), 30000);
      });

      const pageSize = 100;
      const maxPagesPerBatch = 5; // Load only 5 pages at a time to prevent DDOS
      let foundActiveCount = 0;
      let foundDisabledCount = 0;
      const maxActiveLimit = 500; // 5 pages × 100 per page = 500 max per batch
      const maxDisabledLimit = 500;


      // Load active codes first (5 pages max)
      for (let page = 1; page <= maxPagesPerBatch; page++) {
        try {
          const data = await Promise.race([
            fetchAllInviteCodes(page, pageSize, 
              (response) => response, 
              (error) => { throw error; }, 
              false // includeInactive = false for active codes only
            ),
            timeoutPromise
          ]);

          const processedCodes = processInviteData(data, `active codes load page ${page}`);
          
          if (processedCodes && processedCodes.length > 0) {
            // Only collect active codes in this loop
            const pageActive = processedCodes.filter(code => code.activeState === 'Active');
            
            // Add active codes
            const activeToAdd = pageActive.slice(0, Math.max(0, maxActiveLimit - foundActiveCount));
            if (activeToAdd.length > 0) {
              activeCodes = activeCodes.concat(activeToAdd);
              foundActiveCount += activeToAdd.length;
            }
            
            // Stop if we've hit the active limit
            if (foundActiveCount >= maxActiveLimit) {
              break;
            }
            
            // Stop if this page returned less than pageSize (end of data)
            if (processedCodes.length < pageSize) {
              break;
            }
          } else {
            break;
          }
        } catch (pageError) {
          console.error(`❌ Page ${page} failed:`, pageError);
          if (page === 1) {
            throw pageError; // Fail completely if first page fails
          }
          break; // Stop on subsequent page errors but keep already loaded data
        }
      }

      // Check if we can load more active codes - assume yes if we loaded the full batch
      const canLoadMoreActivePages = activeCodes.length > 0 && (foundActiveCount >= maxActiveLimit || activeCodes.length >= pageSize);
      setCanLoadMoreActive(canLoadMoreActivePages);
      
      // Show active codes immediately and stop loading indicator
      if (activeCodes.length > 0) {
        setInviteCodes(activeCodes);
        setActiveCodes(activeCodes);
        setDisabledCodes([]); // Empty for now
        setLoading(false); // Stop loading indicator - active codes are ready
      } else {
        // No active codes found, keep loading for disabled codes
        console.log('No active codes found, continuing to load disabled codes...');
      }

      // Load disabled codes asynchronously in background (don't await this)
      const loadDisabledCodes = async () => {
        try {
          console.log('🔄 Starting background loading of disabled codes...');
          const backgroundDisabledCodes = [];
          let backgroundFoundDisabledCount = 0;
          
          for (let page = 1; page <= maxPagesPerBatch; page++) {
            try {
              const data = await Promise.race([
                fetchAllInviteCodes(page, pageSize, 
                  (response) => response, 
                  (error) => { throw error; }, 
                  true // includeInactive = true for all codes (then filter for expired)
                ),
                new Promise((_, reject) => {
                  setTimeout(() => reject(new Error('Background load timeout')), 15000);
                })
              ]);

              const processedCodes = processInviteData(data, `disabled codes load page ${page}`);
              
              if (processedCodes && processedCodes.length > 0) {
                // Only collect disabled/expired codes in this loop
                const pageDisabled = processedCodes.filter(code => code.activeState !== 'Active');
                
                // Add disabled codes
                const disabledToAdd = pageDisabled.slice(0, Math.max(0, maxDisabledLimit - backgroundFoundDisabledCount));
                if (disabledToAdd.length > 0) {
                  backgroundDisabledCodes.push(...disabledToAdd);
                  backgroundFoundDisabledCount += disabledToAdd.length;
                }
                
                // Stop if we've hit the disabled limit
                if (backgroundFoundDisabledCount >= maxDisabledLimit) {
                  break;
                }
                
                // Stop if this page returned less than pageSize (end of data)
                if (processedCodes.length < pageSize) {
                  break;
                }
              } else {
                break;
              }
            } catch (pageError) {
              console.error(`❌ Disabled codes page ${page} failed:`, pageError);
              break; // Stop on errors but keep already loaded data
            }
          }

          // Check if we can load more disabled codes - assume yes if we loaded the full batch
          const canLoadMoreDisabledPages = backgroundDisabledCodes.length > 0 && (backgroundFoundDisabledCount >= maxDisabledLimit || backgroundDisabledCodes.length >= pageSize);
          setCanLoadMoreDisabled(canLoadMoreDisabledPages);
          
          // Update state only once when ALL disabled codes are loaded
          if (backgroundDisabledCodes.length > 0) {
            setDisabledCodes(backgroundDisabledCodes);
            setInviteCodes(prevCodes => {
              // Only update if we don't already have these disabled codes
              if (prevCodes.length === activeCodes.length) {
                return activeCodes.concat(backgroundDisabledCodes);
              }
              return prevCodes;
            });
            console.log(`✅ Background loading complete: ${backgroundDisabledCodes.length} disabled codes added`);
          }
        } catch (error) {
          console.error('❌ Background disabled codes loading failed:', error);
        }
      };

      // Start background loading after a small delay to ensure active codes are fully rendered
      setTimeout(() => {
        loadDisabledCodes();
      }, 100);
      
      // Only show error if no active codes were found (disabled codes loading in background)
      if (activeCodes.length === 0) {
        setError('No active codes found');
      }
    } catch (error) {
      console.error('❌ Enhanced load failed:', error);
      setError('Failed to load: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Load more active codes (next 5 pages)
  const loadMoreActiveCodes = async () => {
    if (!canLoadMoreActive) return;
    
    setLoading(true);
    try {
      const nextBatch = activeCurrentBatch + 1;
      const startPage = (nextBatch - 1) * maxPagesPerBatch + 1;
      const endPage = nextBatch * maxPagesPerBatch;
      
      let newActiveCodes = [];
      let foundCount = 0;
      
      for (let page = startPage; page <= endPage; page++) {
        try {
          const data = await fetchAllInviteCodes(page, pageSize, 
            (response) => response, 
            (error) => { throw error; }, 
            false // includeInactive = false for active codes only
          );
          
          const processedCodes = processInviteData(data, `load more active page ${page}`);
          
          if (processedCodes && processedCodes.length > 0) {
            const pageActive = processedCodes.filter(code => code.activeState === 'Active');
            if (pageActive.length > 0) {
              newActiveCodes = newActiveCodes.concat(pageActive);
              foundCount += pageActive.length;
            }
            
            // Stop if this page returned less than pageSize (end of data)
            if (processedCodes.length < pageSize) {
              setCanLoadMoreActive(false);
              break;
            }
          } else {
            setCanLoadMoreActive(false);
            break;
          }
        } catch (pageError) {
          console.error(`❌ Load more active page ${page} failed:`, pageError);
          break;
        }
      }
      
      if (newActiveCodes.length > 0) {
        const updatedActiveCodes = [...activeCodes, ...newActiveCodes];
        setActiveCodes(updatedActiveCodes);
        setInviteCodes([...updatedActiveCodes, ...disabledCodes]);
        setActiveCurrentBatch(nextBatch);
      }
      
      // Check if we can still load more
      if (foundCount < maxActiveLimit) {
        setCanLoadMoreActive(false);
      }
    } catch (error) {
      console.error('❌ Load more active codes failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load more disabled codes (next 5 pages)
  const loadMoreDisabledCodes = async () => {
    if (!canLoadMoreDisabled) return;
    
    setLoading(true);
    try {
      const nextBatch = disabledCurrentBatch + 1;
      const startPage = (nextBatch - 1) * maxPagesPerBatch + 1;
      const endPage = nextBatch * maxPagesPerBatch;
      
      let newDisabledCodes = [];
      let foundCount = 0;
      
      for (let page = startPage; page <= endPage; page++) {
        try {
          const data = await fetchAllInviteCodes(page, pageSize, 
            (response) => response, 
            (error) => { throw error; }, 
            true // includeInactive = true for all codes (then filter for expired)
          );
          
          const processedCodes = processInviteData(data, `load more disabled page ${page}`);
          
          if (processedCodes && processedCodes.length > 0) {
            const pageDisabled = processedCodes.filter(code => code.activeState !== 'Active');
            if (pageDisabled.length > 0) {
              newDisabledCodes = newDisabledCodes.concat(pageDisabled);
              foundCount += pageDisabled.length;
            }
            
            // Stop if this page returned less than pageSize (end of data)
            if (processedCodes.length < pageSize) {
              setCanLoadMoreDisabled(false);
              break;
            }
          } else {
            setCanLoadMoreDisabled(false);
            break;
          }
        } catch (pageError) {
          console.error(`❌ Load more disabled page ${page} failed:`, pageError);
          break;
        }
      }
      
      if (newDisabledCodes.length > 0) {
        const updatedDisabledCodes = [...disabledCodes, ...newDisabledCodes];
        setDisabledCodes(updatedDisabledCodes);
        setInviteCodes([...activeCodes, ...updatedDisabledCodes]);
        setDisabledCurrentBatch(nextBatch);
      }
      
      // Check if we can still load more
      if (foundCount < maxDisabledLimit) {
        setCanLoadMoreDisabled(false);
      }
    } catch (error) {
      console.error('❌ Load more disabled codes failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Prioritized loader - Active codes first, then Disabled codes in background
  const loadInviteCodesPrioritized = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Load timeout after 30 seconds')), 30000);
      });
      const pageSize = 100;
      const maxActiveLimit = 1000;
      const maxDisabledLimit = 1000;
      
      let allActiveCodes = [];
      let foundActiveCount = 0;
      let currentPage = 1;
      
      // Phase 1: Load Active codes first (priority)
      
      while (foundActiveCount < maxActiveLimit && currentPage <= 20) {
        try {
          const data = await Promise.race([
            getAllInviteCodes(currentPage, pageSize, true, showAllCodes),
            timeoutPromise
          ]);
          
          const processedCodes = processInviteData(data, `prioritized load page ${currentPage}`);
          
          if (processedCodes && processedCodes.length > 0) {
            const pageActive = processedCodes.filter(code => code.activeState === 'Active');
            
            if (pageActive.length > 0) {
              const activeToAdd = pageActive.slice(0, Math.max(0, maxActiveLimit - foundActiveCount));
              allActiveCodes = allActiveCodes.concat(activeToAdd);
              foundActiveCount += activeToAdd.length;
            }
            
            // If page is smaller than pageSize, we've reached end of data
            if (processedCodes.length < pageSize) {
              break;
            }
          } else {
            break;
          }
          
          currentPage++;
        } catch (pageError) {
          console.error(`❌ Page ${currentPage} failed:`, pageError);
          if (currentPage === 1) {
            throw pageError; // Fail if first page fails
          }
          break;
        }
      }
      
      // Show Active codes immediately
      setActiveCodes(allActiveCodes);
      setInviteCodes(allActiveCodes); // Show only active codes initially
      setLoading(false); // Stop loading indicator - active codes are ready!
      
      // Phase 2: Load Disabled codes in background (non-blocking)
      setTimeout(async () => {
        try {
          setBackgroundLoading(true);
          let allDisabledCodes = [];
          let foundDisabledCount = 0;
          let bgCurrentPage = 1;
          
          while (foundDisabledCount < maxDisabledLimit && bgCurrentPage <= 20) {
            try {
              const data = await getAllInviteCodes(bgCurrentPage, pageSize, true, showAllCodes);
              const processedCodes = processInviteData(data, `background load page ${bgCurrentPage}`);
              
              if (processedCodes && processedCodes.length > 0) {
                const pageDisabled = processedCodes.filter(code => code.activeState !== 'Active');
                
                if (pageDisabled.length > 0) {
                  const disabledToAdd = pageDisabled.slice(0, Math.max(0, maxDisabledLimit - foundDisabledCount));
                  allDisabledCodes = allDisabledCodes.concat(disabledToAdd);
                  foundDisabledCount += disabledToAdd.length;
                }
                
                if (processedCodes.length < pageSize) {
                  break;
                }
              } else {
                break;
              }
              
              bgCurrentPage++;
            } catch (bgPageError) {
              console.error(`❌ Background page ${bgCurrentPage} failed:`, bgPageError);
              break;
            }
          }
          
          // Update with both active and disabled codes
          setDisabledCodes(allDisabledCodes);
          const combinedCodes = allActiveCodes.concat(allDisabledCodes);
          setInviteCodes(combinedCodes);
          
        } catch (bgError) {
          console.error('❌ Background disabled codes loading failed:', bgError);
          // Don't show error - active codes are already loaded
        } finally {
          setBackgroundLoading(false);
        }
      }, 100); // Small delay to let active codes render first
      
    } catch (error) {
      console.error('❌ Prioritized loading failed:', error);
      setError('Failed to load active codes: ' + error.message);
      setLoading(false);
    }
  };

  // Load all codes and store them separately for pagination
  const [allActiveCodesCache, setAllActiveCodesCache] = useState([]);
  const [allDisabledCodesCache, setAllDisabledCodesCache] = useState([]);
  const [allDeletedCodesCache, setAllDeletedCodesCache] = useState([]);
  
  // Initial load function - optimized to load first pages quickly
  const loadAllCodesInitial = async () => {
    setLoading(true);
    try {
      // First, load enough items to display the first page of each table
      // Plus extra for smooth initial pagination (2 pages worth per table = 6 pages total)
      const initialLoadSize = ITEMS_PER_PAGE * 6; // 120 items
      const firstPageData = await getAllInviteCodes(1, initialLoadSize, true, showAllCodes);
      const firstCodes = processInviteData(firstPageData, 'initial load');
      
      if (firstCodes && firstCodes.length > 0) {
        // Separate into 3 categories and show first page immediately
        const activeCodesInitial = firstCodes.filter(code => code.activeState === 'Active');
        const disabledCodesInitial = firstCodes.filter(code => code.activeState !== 'Active' && code.activeState !== 'Deleted');
        const deletedCodesInitial = firstCodes.filter(code => code.activeState === 'Deleted');
        
        // Show first page right away
        setActiveCodes(activeCodesInitial.slice(0, ITEMS_PER_PAGE));
        setDisabledCodes(disabledCodesInitial.slice(0, ITEMS_PER_PAGE));
        setDeletedCodes(deletedCodesInitial.slice(0, ITEMS_PER_PAGE));
        
        // Set initial totals based on what we have so far
        setActiveCodesTotal(activeCodesInitial.length);
        setDisabledCodesTotal(disabledCodesInitial.length);
        setDeletedCodesTotal(deletedCodesInitial.length);
        setActiveTotalPages(Math.ceil(activeCodesInitial.length / ITEMS_PER_PAGE));
        setDisabledTotalPages(Math.ceil(disabledCodesInitial.length / ITEMS_PER_PAGE));
        setDeletedTotalPages(Math.ceil(deletedCodesInitial.length / ITEMS_PER_PAGE));
        
        // Also update cache with initial data
        setAllActiveCodesCache(activeCodesInitial);
        setAllDisabledCodesCache(disabledCodesInitial);
        setAllDeletedCodesCache(deletedCodesInitial);
        
        // Now reload the current pages from cache to ensure consistency
        loadActivePageFromCache(1, activeCodesInitial);
        loadDisabledPageFromCache(1, disabledCodesInitial);
        loadDeletedPageFromCache(1, deletedCodesInitial);
        
        setLoading(false); // User can start interacting
        
        // Load remaining data in background
        loadRemainingCodesInBackground();
      }
    } catch (error) {
      setError('Failed to load codes: ' + error.message);
      setLoading(false);
    }
  };
  
  // Background loading for complete dataset
  const loadRemainingCodesInBackground = async () => {
    try {
      setBackgroundLoading(true);
      let allCodes = [];
      let currentPage = 1;
      const pageSize = 100; // API page size
      const maxPages = 20; // Safety limit: 20 pages * 100 = 2000 codes max
      
      while (currentPage <= maxPages) {
        const data = await getAllInviteCodes(currentPage, pageSize, true, showAllCodes);
        const processedCodes = processInviteData(data, `background page ${currentPage}`);
        
        if (processedCodes && processedCodes.length > 0) {
          allCodes = allCodes.concat(processedCodes);
          
          // Update cache progressively - separate into 3 categories
          const activeSoFar = allCodes.filter(code => code.activeState === 'Active');
          const disabledSoFar = allCodes.filter(code => code.activeState !== 'Active' && code.activeState !== 'Deleted');
          const deletedSoFar = allCodes.filter(code => code.activeState === 'Deleted');
          
          setAllActiveCodesCache(activeSoFar);
          setAllDisabledCodesCache(disabledSoFar);
          setAllDeletedCodesCache(deletedSoFar);
          setActiveCodesTotal(activeSoFar.length);
          setDisabledCodesTotal(disabledSoFar.length);
          setDeletedCodesTotal(deletedSoFar.length);
          setActiveTotalPages(Math.ceil(activeSoFar.length / ITEMS_PER_PAGE));
          setDisabledTotalPages(Math.ceil(disabledSoFar.length / ITEMS_PER_PAGE));
          setDeletedTotalPages(Math.ceil(deletedSoFar.length / ITEMS_PER_PAGE));
          
          // Update the currently displayed pages with fresh data
          loadActivePageFromCache(activeCurrentPage, activeSoFar);
          loadDisabledPageFromCache(disabledCurrentPage, disabledSoFar);
          loadDeletedPageFromCache(deletedCurrentPage, deletedSoFar);
          
          // Only break if we get 0 codes (empty page = no more data)
          // Don't break on < pageSize because the last page might be partial
          // Example: 205 total codes = page 1: 100, page 2: 100, page 3: 5
          if (processedCodes.length === 0) break;
          currentPage++;
        } else {
          break;
        }
      }
    } catch (error) {
      // Silent fail for background loading
    } finally {
      setBackgroundLoading(false);
    }
  };
  
  // Load active codes page from cache
  const loadActivePageFromCache = (page, cache = allActiveCodesCache) => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const pageData = cache.slice(startIndex, endIndex);
    setActiveCodes(pageData);
  };
  
  // Load disabled codes page from cache
  const loadDisabledPageFromCache = (page, cache = allDisabledCodesCache) => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const pageData = cache.slice(startIndex, endIndex);
    setDisabledCodes(pageData);
  };

  // Load deleted codes page from cache
  const loadDeletedPageFromCache = (page, cache = allDeletedCodesCache) => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const pageData = cache.slice(startIndex, endIndex);
    setDeletedCodes(pageData);
  };

  // Load all codes on mount
  useEffect(() => {
    loadAllCodesInitial();
  }, []);

  // Initialize allowlist on component mount
  useEffect(() => {
    const init = async () => {
      await initializeAllowlist();
    };
    init();
  }, []);
  
  
  
  // Pagination handlers with smooth scrolling
  const handleActivePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= activeTotalPages) {
      setActiveCurrentPage(newPage);
      loadActivePageFromCache(newPage);
      // Scroll to table with offset to ensure header is visible
      if (activeTableRef.current) {
        const elementPosition = activeTableRef.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 100; // 100px offset from top
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };
  
  const handleDisabledPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= disabledTotalPages) {
      setDisabledCurrentPage(newPage);
      loadDisabledPageFromCache(newPage);
      // Scroll to table with offset to ensure header is visible
      if (disabledTableRef.current) {
        const elementPosition = disabledTableRef.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 100; // 100px offset from top
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };
  
  const handleDeletedPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= deletedTotalPages) {
      setDeletedCurrentPage(newPage);
      loadDeletedPageFromCache(newPage);
      // Scroll to table with offset to ensure header is visible
      if (deletedTableRef.current) {
        const elementPosition = deletedTableRef.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 100; // 100px offset from top
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  // Authentication and organization check
  useEffect(() => {
    const checkAuth = async () => {
      const user = getCurrentUser();
      
      // Debug logging
      
      if (!isCurrenAuthUserLoading) {
        if (user && user.email) {
          // Check if user email is from your organization
          const userEmail = user.email || '';
          const allowedDomains = ['@musicmakesusfeels.com', '@feelsmusic.com'];
          
          const isDomainAuthorized = allowedDomains.some(domain => userEmail.endsWith(domain));
          
          if (!isDomainAuthorized) {
            setAuthError(`Access denied. Only users with @musicmakesusfeels.com or @feelsmusic.com email addresses can access this page.`);
            setLoading(false);
          } else {
            // Check allowlist
            const isInAllowlist = await isUserAllowed(userEmail);
            
            
            if (!isInAllowlist) {
              setAuthError(`Access denied. Your account (${userEmail}) is not authorized to access this page. Please contact an administrator.`);
              setLoading(false);
            }
          }
        } else {
          setAuthError('Please log in to access this page.');
          setLoading(false);
        }
      }
    };
    
    checkAuth();
  }, [getCurrentUser, isCurrenAuthUserLoading]);

  const toggleAllCodes = async () => {
    const newShowAllCodes = !showAllCodes;
    setShowAllCodes(newShowAllCodes);
    
    // Reset everything and show loading state
    setLoading(true);
    setError(null);
    setBackgroundLoading(true);
    
    // Clear existing data and show "calculating" states
    setActiveCodes([]);
    setDisabledCodes([]);
    setDeletedCodes([]);
    setActiveCodesTotal(0);
    setDisabledCodesTotal(0);
    setDeletedCodesTotal(0);
    setActiveTotalPages(1);
    setDisabledTotalPages(1);
    setDeletedTotalPages(1);
    
    try {
      // First load initial batch to show something quickly
      const initialPageSize = 100;
      
      // Create temporary function that uses the new state
      const tempGetAllInviteCodes = (pageNumber, pageLimit, includeInactive = true) => {
        return getAllInviteCodes(pageNumber, pageLimit, includeInactive, newShowAllCodes);
      };
      
      // Load first page to show something immediately
      const firstPageData = await tempGetAllInviteCodes(1, initialPageSize, true);
      const firstCodes = processInviteData(firstPageData, 'toggle initial load');
      
      if (firstCodes && firstCodes.length > 0) {
        // Separate first batch into categories
        const activeCodesFirst = firstCodes.filter(code => code.activeState === 'Active');
        const disabledCodesFirst = firstCodes.filter(code => code.activeState !== 'Active' && code.activeState !== 'Deleted');
        const deletedCodesFirst = firstCodes.filter(code => code.activeState === 'Deleted');
        
        // Show first page immediately with "calculating" totals
        setActiveCodes(activeCodesFirst.slice(0, 20));
        setDisabledCodes(disabledCodesFirst.slice(0, 20));
        setDeletedCodes(deletedCodesFirst.slice(0, 20));
        setActiveCodesTotal(activeCodesFirst.length);
        setDisabledCodesTotal(disabledCodesFirst.length);
        setDeletedCodesTotal(deletedCodesFirst.length);
        setLoading(false); // Stop main loading, but keep background loading
        
        // Now load remaining pages in background
        let allCodes = [...firstCodes];
        let currentPage = 2;
        const maxPages = 50;
        
        while (currentPage <= maxPages) {
          try {
            const data = await tempGetAllInviteCodes(currentPage, initialPageSize, true);
            const processedCodes = processInviteData(data, `toggle background page ${currentPage}`);
            
            if (processedCodes && processedCodes.length > 0) {
              allCodes = allCodes.concat(processedCodes);
              currentPage++;
              
              // Update totals as we load more
              const activeCodesAll = allCodes.filter(code => code.activeState === 'Active');
              const disabledCodesAll = allCodes.filter(code => code.activeState !== 'Active' && code.activeState !== 'Deleted');
              const deletedCodesAll = allCodes.filter(code => code.activeState === 'Deleted');
              
              // Update totals and pagination info
              setActiveCodesTotal(activeCodesAll.length);
              setDisabledCodesTotal(disabledCodesAll.length);
              setDeletedCodesTotal(deletedCodesAll.length);
              
              const activePages = Math.ceil(activeCodesAll.length / 20);
              const disabledPages = Math.ceil(disabledCodesAll.length / 20);
              const deletedPages = Math.ceil(deletedCodesAll.length / 20);
              
              setActiveTotalPages(activePages);
              setDisabledTotalPages(disabledPages);
              setDeletedTotalPages(deletedPages);
              
              // Update caches
              setAllActiveCodesCache(activeCodesAll);
              setAllDisabledCodesCache(disabledCodesAll);
              setAllDeletedCodesCache(deletedCodesAll);
              
              // If we got less than the page size, we've reached the end
              if (processedCodes.length < initialPageSize) {
                break;
              }
            } else {
              // No more data
              break;
            }
          } catch (err) {
            console.error(`Background load failed for page ${currentPage}:`, err);
            break;
          }
        }
        
        // Final setup
        const finalActiveCodesAll = allCodes.filter(code => code.activeState === 'Active');
        const finalDisabledCodesAll = allCodes.filter(code => code.activeState !== 'Active' && code.activeState !== 'Deleted');
        const finalDeletedCodesAll = allCodes.filter(code => code.activeState === 'Deleted');
        
        // Final cache update and pagination setup
        setAllActiveCodesCache(finalActiveCodesAll);
        setAllDisabledCodesCache(finalDisabledCodesAll);
        setAllDeletedCodesCache(finalDeletedCodesAll);
        
        setActiveCodesTotal(finalActiveCodesAll.length);
        setDisabledCodesTotal(finalDisabledCodesAll.length);
        setDeletedCodesTotal(finalDeletedCodesAll.length);
        
        const finalActivePages = Math.ceil(finalActiveCodesAll.length / 20);
        const finalDisabledPages = Math.ceil(finalDisabledCodesAll.length / 20);
        const finalDeletedPages = Math.ceil(finalDeletedCodesAll.length / 20);
        
        setActiveTotalPages(finalActivePages);
        setDisabledTotalPages(finalDisabledPages);
        setDeletedTotalPages(finalDeletedPages);
        
        setCanLoadMoreActive(finalActivePages > 1);
        setCanLoadMoreDisabled(finalDisabledPages > 1);
      }
    } catch (err) {
      setError('Failed to load codes: ' + err.message);
      setLoading(false);
    } finally {
      setBackgroundLoading(false);
    }
  };

  const processInviteData = (data, source = 'unknown') => {
    
    // Try different possible response structures
    let inviteData = null;
    if (data.app_invites && Array.isArray(data.app_invites)) {
      inviteData = data.app_invites;
    } else if (data.invites && Array.isArray(data.invites)) {
      inviteData = data.invites;
    } else if (data.data && Array.isArray(data.data)) {
      inviteData = data.data;
    } else if (Array.isArray(data)) {
      inviteData = data;
    }
    
    if (inviteData && Array.isArray(inviteData)) {
      const formattedCodes = inviteData.map((invite, index) => {
        
        // Determine if invite is active based on expiration, deletion, and signup limits
        const isExpired = invite.date_expire && new Date(invite.date_expire) <= new Date();
        const isDeleted = invite.is_deleted === true;
        const inviteLimit = invite.max_signup_count || invite.invite_limit || invite.limit || 0;
        const usageCount = invite.signup_count || invite.usage_count || invite.used_count || invite.uses || 0;
        const hasReachedMaxSignups = inviteLimit > 0 && usageCount >= inviteLimit;
        
        
        // Calculate active state dynamically (immutable)
        let activeState = 'Active';
        let disableReason = '';
        if (isDeleted) {
          activeState = 'Deleted';
          disableReason = 'Deleted';
        } else if (isExpired) {
          activeState = 'Expired';
          disableReason = 'Expired';
        } else if (hasReachedMaxSignups) {
          activeState = 'Spots Filled';
          disableReason = 'Spots Filled';
        }
        
        const isActive = activeState === 'Active';
        
        // Handle expiration date - check multiple possible field names
        let expirationDate = 'No expiration';
        const dateFields = ['date_expire', 'expiration_date', 'expire_date', 'date_expiry'];
        
        for (const field of dateFields) {
          if (invite[field]) {
            try {
              const date = new Date(invite[field]);
              if (!isNaN(date.getTime())) {
                expirationDate = date.toLocaleDateString();
                break;
              }
            } catch (e) {
              console.warn(`Failed to parse date field ${field}:`, invite[field]);
            }
          }
        }
        
        // Special check for FEELS/FEELSNULL codes
        if ((invite.invite_token || invite.token) === 'FEELS' || (invite.invite_token || invite.token) === 'FEELSNULL') {
          // Handle special case if needed
        }
        
        
        // Format creation date
        let creationDate = 'N/A';
        const dateCreatedFields = ['date_created', 'created_at', 'creation_date'];
        for (const field of dateCreatedFields) {
          if (invite[field]) {
            try {
              const date = new Date(invite[field]);
              if (!isNaN(date.getTime())) {
                creationDate = date.toLocaleDateString();
                break;
              }
            } catch (e) {
              console.warn(`Failed to parse creation date field ${field}:`, invite[field]);
            }
          }
        }

        // Determine multi signup status
        const multiSignup = invite.allow_multiple_signup === true ? 'Yes' : 'No';

        // Extract song artist name and video URL for Ext. Link, and f_id for FAST FEELS
        let songArtistName = 'N/A';
        let videoUrl720x1280 = null;
        let fastFeelsUrl = null;
        
        if (invite.feels && typeof invite.feels === 'object') {
          songArtistName = invite.feels.song_artist_name || 'N/A';
          
          // Extract f_id for FAST FEELS URL
          if (invite.feels.f_id) {
            fastFeelsUrl = `https://admin.feelsmusic.app/feel/${invite.feels.f_id}`;
          }
          
          // Look for 720 video URL in the new nested structure
          if (invite.feels.preview && invite.feels.preview.video && invite.feels.preview.video['720'] && invite.feels.preview.video['720'].url) {
            videoUrl720x1280 = invite.feels.preview.video['720'].url;
          }
          // Fallback to other possible locations
          else if (invite.feels.video_urls) {
            videoUrl720x1280 = invite.feels.video_urls['720x1280'] || 
                              invite.feels.video_urls['720_1280'] ||
                              invite.feels.video_urls['720'] ||
                              null;
          } else if (invite.feels.video_url_720x1280) {
            videoUrl720x1280 = invite.feels.video_url_720x1280;
          } else if (invite.feels.video_url) {
            videoUrl720x1280 = invite.feels.video_url;
          }
        }

        // Extract creator information
        let creatorName = 'N/A';
        if (invite.feels && invite.feels.creator_user) {
          const creator = invite.feels.creator_user;
          creatorName = creator.name || creator.username || 'N/A';
        }

        return {
          inviteCode: invite.invite_token || invite.token || invite.code || invite.invite_code || '',
          inviteLimit: inviteLimit,
          usageCount: usageCount,
          expirationDate: expirationDate,
          activeState: activeState, // Now immutable and calculated
          actions: isActive ? '' : 'Reactivate', // Empty for active codes (dropdown shows options), Reactivate for disabled
          // notes: invite.notes || invite.description || '',
          geoRestriction: invite.geo_restricted !== undefined 
            ? (invite.geo_restricted ? 'selected_countries' : 'worldwide')
            : (invite.geo_restriction ? (invite.geo_restriction === true ? 'selected_countries' : 'worldwide') : 'worldwide'), // Handle both field names and missing field
          referralGroupObject: invite.referral_group_object || 'N/A',
          creator: creatorName,
          dateCreated: creationDate,
          multiSignup: multiSignup,
          disableReason: disableReason, // New field for disabled codes table
          feels: invite.feels || null, // New feels field from updated API
          songArtistName: songArtistName,
          videoUrl720x1280: videoUrl720x1280,
          fastFeelsUrl: fastFeelsUrl,
        };
      });
      
      return formattedCodes;
    }
    
    return null;
  };

  const filterCodes = (codes) => {
    const active = codes.filter(code => code.activeState === 'Active');
    const disabled = codes.filter(code => code.activeState !== 'Active'); // Everything else (Expired, Deleted, Spots Filled)
    
    setActiveCodes(active);
    setDisabledCodes(disabled);
  };


  const loadInviteCodes = async () => {
    setLoading(true);
    setError(null);


    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('API call timed out after 30 seconds')), 30000);
      });

      // Try the modern promise-based approach first
      
      // First, get initial page to understand pagination - include inactive codes
      const firstPageData = await Promise.race([
        getAllInviteCodes(1, 100, true, showAllCodes),
        timeoutPromise
      ]);
      
      let allCodes = [];
      const pageLimit = 100; // Use smaller page size
      let currentPage = 1;
      let hasMorePages = true;
      
      // If API returns total count, use it to determine pages
      const totalCount = firstPageData.invites_count || firstPageData.total_count || 0;
      
      // Fetch all pages
      while (hasMorePages) {
        try {
          const data = await Promise.race([
            getAllInviteCodes(currentPage, pageLimit, true, showAllCodes),
            timeoutPromise
          ]);
          const processedCodes = processInviteData(data, `modern API page ${currentPage}`);
          
          if (processedCodes && processedCodes.length > 0) {
            allCodes = allCodes.concat(processedCodes);
            
            // Check if we have more pages
            if (processedCodes.length < pageLimit || (totalCount > 0 && allCodes.length >= totalCount)) {
              hasMorePages = false;
            } else {
              currentPage++;
            }
          } else {
            hasMorePages = false;
          }
          
          // Safety check to prevent infinite loops
          if (currentPage > 50) {
            console.warn('Stopping pagination after 50 pages to prevent infinite loop');
            hasMorePages = false;
          }
        } catch (pageError) {
          console.error(`Error fetching page ${currentPage}:`, pageError);
          hasMorePages = false;
        }
      }
      
      if (allCodes.length > 0) {
        setInviteCodes(allCodes);
        filterCodes(allCodes);
        setLoading(false);
        return;
      }
      
      
      // If that doesn't work, try alternative endpoints with inactive codes
      const alternativeResults = await tryAlternativeInviteEndpoints(1, 100, true);
      
      for (const result of alternativeResults) {
        if (result.status === 'fulfilled' && result.value.success) {
          const processedCodes = processInviteData(result.value.data, result.value.endpoint);
          if (processedCodes) {
            setInviteCodes(processedCodes);
            filterCodes(processedCodes);
            setLoading(false);
            return;
          }
        }
      }
      
      // If all else fails, try the original callback approach
      fetchAllInviteCodes(
        1, // page number
        100, // page limit
        (response) => {
          response.json().then((data) => {
            const processedCodes = processInviteData(data, 'callback API');
            if (processedCodes) {
              setInviteCodes(processedCodes);
              filterCodes(processedCodes);
            } else {
              setError('No invite codes found in any API response format. Check console for details.');
            }
            setLoading(false);
          }).catch((err) => {
            console.error('JSON parsing error:', err);
            setError('Failed to parse invite codes data: ' + err.message);
            setLoading(false);
          });
        },
        (errorMsg) => {
          console.error('Callback API Error:', errorMsg);
          setError(`All API approaches failed. Last error: ${errorMsg || 'Failed to fetch invite codes'}`);
          setLoading(false);
        },
      );
      
    } catch (error) {
      console.error('Promise-based API error:', error);
      
      // Fallback to original callback approach
      fetchAllInviteCodes(
        1, // page number
        100, // page limit
        (response) => {
          response.json().then((data) => {
            const processedCodes = processInviteData(data, 'fallback callback API');
            if (processedCodes) {
              setInviteCodes(processedCodes);
              filterCodes(processedCodes);
            } else {
              setError('No invite codes found in API response. Check console for details.');
            }
            setLoading(false);
          }).catch((err) => {
            console.error('Fallback JSON parsing error:', err);
            setError('Failed to parse invite codes data: ' + err.message);
            setLoading(false);
          });
        },
        (errorMsg) => {
          console.error('Fallback API Error:', errorMsg);
          setError(`API Error: ${errorMsg || 'Failed to fetch invite codes'}`);
          setLoading(false);
        },
      );
    }
  };

  const handleSort = (field, table = 'active') => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);

    const sortFunction = (a, b) => {
      if (direction === 'asc') {
        return a[field] > b[field] ? 1 : -1;
      } else {
        return a[field] < b[field] ? 1 : -1;
      }
    };

    if (table === 'active') {
      const sortedData = [...activeCodes].sort(sortFunction);
      setActiveCodes(sortedData);
    } else {
      const sortedData = [...disabledCodes].sort(sortFunction);
      setDisabledCodes(sortedData);
    }
  };

  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortDirection === 'asc' ? ' ↑' : ' ↓';
    }
    return ' ▼';
  };

  // Temporary workaround: Search for existing code details when we get a 409
  const findExistingCodeDetails = async (codeToFind) => {
    try {
      // Search through our local data first (faster)
      const searchInArray = (codesArray, defaultState) => {
        const found = codesArray.find(code => 
          code.inviteCode && code.inviteCode.toUpperCase() === codeToFind.toUpperCase()
        );
        if (found) {
          return {
            type: found.referralGroupObject || 'unknown',
            state: found.activeState || defaultState
          };
        }
        return null;
      };

      // Check local arrays first
      let result = searchInArray(activeCodes, 'active') || 
                   searchInArray(disabledCodes, 'disabled') ||
                   searchInArray(deletedCodes, 'deleted');
      
      if (result) return result;

      // If not found locally, it's likely a hidden user_feels_send code
      console.log('Code not found in locally loaded data - likely a hidden user_feels_send code');
      
      // Since user_feels_send codes are hidden and not loaded in our interface,
      // we can make an educated guess that an unfindable duplicate is probably user_feels_send
      return {
        type: 'user_feels_send (inferred)', 
        state: 'active (hidden from interface)'
      };
    } catch (error) {
      console.error('Error finding existing code details:', error);
      return {
        type: 'unknown',
        state: 'unknown'
      };
    }
  };

  const generateSuggestedCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let attempts = 0;
    const maxAttempts = 100; // Prevent infinite loop
    
    while (attempts < maxAttempts) {
      let result = '';
      for (let i = 0; i < 6; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      
      // Check if this code already exists in loaded data
      const existsInActive = activeCodes.some(code => 
        code.inviteCode && code.inviteCode.toUpperCase() === result.toUpperCase()
      );
      const existsInDisabled = disabledCodes.some(code => 
        code.inviteCode && code.inviteCode.toUpperCase() === result.toUpperCase()
      );
      const existsInDeleted = deletedCodes.some(code => 
        code.inviteCode && code.inviteCode.toUpperCase() === result.toUpperCase()
      );
      
      // If unique, return it
      if (!existsInActive && !existsInDisabled && !existsInDeleted) {
        return result;
      }
      
      attempts++;
    }
    
    // If we couldn't find a unique code after maxAttempts, make it longer
    console.warn('Could not generate unique 6-char code, using 8 characters');
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const addNewCode = () => {
    setNewCodeData({
      inviteCode: '',
      inviteLimit: '',
      expirationDate: '',
      geoRestriction: 'worldwide',
      allowMultipleSignup: true,
      referralGroupObject: 'artist',
      feelsId: ''
    });
    setShowAdvancedOptions(false);
    setSuggestedCode(generateSuggestedCode());
    setShowNewCodeModal(true);
  };

  const handleNewCodeSave = async () => {
    // Clear any previous modal errors
    setModalError('');
    
    // Validate required fields
    if (!newCodeData.inviteLimit || parseInt(newCodeData.inviteLimit) < 1) {
      setModalError('Invite limit must be at least 1');
      return;
    }

    // Additional validation
    const inviteLimit = parseInt(newCodeData.inviteLimit);
    if (isNaN(inviteLimit) || inviteLimit < 1) {
      setModalError('Invalid invite limit. Must be a number greater than 0.');
      return;
    }

    // Use custom code if provided, otherwise use the suggested code that was already generated
    const finalInviteCode = newCodeData.inviteCode || suggestedCode;

    // Prepare expiration date for API
    let formattedExpirationDate = null;
    let displayExpirationDate = 'No expiration';
    
    if (newCodeData.expirationDate) {
      if (!newCodeData.expirationDate.includes('T')) {
        formattedExpirationDate = newCodeData.expirationDate + 'T00:00:00.000Z';
      } else {
        formattedExpirationDate = newCodeData.expirationDate;
      }
      displayExpirationDate = new Date(newCodeData.expirationDate).toLocaleDateString();
    } else {
      formattedExpirationDate = null;
    }
    
    // Create feels object if FeelsID is provided
    let feelsObject = null;
    let songArtistName = 'N/A';
    let fastFeelsUrl = null;
    
    if (newCodeData.feelsId && newCodeData.feelsId.trim()) {
      feelsObject = {
        f_id: newCodeData.feelsId.trim()
      };
      songArtistName = 'Custom Feel'; // Placeholder since we don't have other data
      fastFeelsUrl = `https://admin.feelsmusic.app/feel/${newCodeData.feelsId.trim()}`;
    }

    // Show loading state
    setIsSavingSong(true);
    
    // Call API to create the code - wait for response before updating UI
    createInviteCode(
      inviteLimit,
      formattedExpirationDate,
      (response) => {
        // Success - add the new code to the UI
        setIsSavingSong(false);
        
        // Parse response to get the created code details
        response.json().then(data => {
          // Create the new code object from server response
          const newCode = {
            inviteCode: data.inviteCode || finalInviteCode,
            inviteLimit: data.inviteLimit || inviteLimit,
            usageCount: data.usageCount || 0,
            expirationDate: data.expirationDate || displayExpirationDate,
            activeState: data.activeState || 'Active',
            actions: '',
            geoRestriction: newCodeData.geoRestriction || 'worldwide',
            referralGroupObject: data.referralGroupObject || newCodeData.referralGroupObject || 'user_feels_send',
            creator: data.creator || 'N/A',
            dateCreated: data.dateCreated || new Date().toLocaleDateString(),
            multiSignup: data.multiSignup || (newCodeData.allowMultipleSignup ? 'Yes' : 'No'),
            disableReason: data.disableReason || '',
            feels: feelsObject,
            songArtistName: songArtistName,
            videoUrl720x1280: data.videoUrl720x1280 || null,
            fastFeelsUrl: fastFeelsUrl
          };

          // Add to cache and refresh current page
          const updatedCache = [newCode, ...allActiveCodesCache];
          setAllActiveCodesCache(updatedCache);
          setActiveCodesTotal(prev => prev + 1);
          setActiveTotalPages(Math.ceil((activeCodesTotal + 1) / ITEMS_PER_PAGE));
          
          // Update the main inviteCodes array
          const updatedInviteCodes = [newCode, ...inviteCodes];
          setInviteCodes(updatedInviteCodes);
          
          // If on first page, show the new code
          if (activeCurrentPage === 1) {
            loadActivePageFromCache(1, updatedCache);
          }
          
          // Close modal and reset
          setShowNewCodeModal(false);
          setNewCodeData({
            inviteCode: '',
            inviteLimit: '',
            expirationDate: '',
            geoRestriction: 'worldwide',
            allowMultipleSignup: true,
            referralGroupObject: 'artist',
            feelsId: ''
          });
          setShowAdvancedOptions(false);
          setSuggestedCode('');
          
          // Show success message
          console.log('✅ Code created successfully:', data.inviteCode || finalInviteCode);
        }).catch(parseError => {
          console.error('Failed to parse response:', parseError);
          setModalError('Code created but failed to update display. Please refresh.');
        });
      },
      (errorResponse) => {
        // Error - handle based on status code
        setIsSavingSong(false);
        
        console.log('Error response received:', errorResponse);
        
        // Handle 409 conflict errors (duplicate codes)
        if (typeof errorResponse === 'object' && errorResponse?.status === 409) {
          // New structured 409 error from API context
          const errorData = errorResponse.data || {};
          
          // Extract type and state information from the response data
          let existingType = errorData.type || 
                            errorData.referralGroupObject || 
                            errorData.referral_group_object ||
                            errorData.code_type ||
                            'unknown';
                            
          let existingState = errorData.state || 
                             errorData.activeState || 
                             errorData.active_state ||
                             errorData.code_state ||
                             'unknown';
          
          // If backend didn't provide details, use our workaround
          if (existingType === 'unknown' && existingState === 'unknown') {
            console.log('Backend provided no metadata, using workaround to find existing code...');
            
            findExistingCodeDetails(finalInviteCode).then(details => {
              const detailedType = details.type;
              const detailedState = details.state;
              
              if (detailedType === 'user_feels_send' || detailedType === 'user_send_feels') {
                setModalError(`Code "${finalInviteCode}" already exists (Type: ${detailedType}, State: ${detailedState}). This code may be hidden from the normal view.`);
              } else {
                setModalError(`Code "${finalInviteCode}" already exists (Type: ${detailedType}, State: ${detailedState}).`);
              }
            }).catch(() => {
              // Fallback if search fails
              setModalError(`Code "${finalInviteCode}" already exists. Unable to determine type and state.`);
            });
            
            // Show initial message while searching
            setModalError(`Code "${finalInviteCode}" already exists. Checking details...`);
          } else {
            // Backend provided details, use them directly
            if (existingType === 'user_feels_send' || existingType === 'user_send_feels') {
              setModalError(`Code "${finalInviteCode}" already exists (Type: ${existingType}, State: ${existingState}). This code may be hidden from the normal view.`);
            } else {
              setModalError(`Code "${finalInviteCode}" already exists (Type: ${existingType}, State: ${existingState}).`);
            }
          }
          
        } else if (typeof errorResponse === 'string' && errorResponse.includes('409')) {
          // Legacy string-based 409 error - use workaround to get details
          console.log('Legacy 409 string error, using workaround to find existing code...');
          
          findExistingCodeDetails(finalInviteCode).then(details => {
            const detailedType = details.type;
            const detailedState = details.state;
            
            if (detailedType === 'user_feels_send' || detailedType === 'user_send_feels') {
              setModalError(`Code "${finalInviteCode}" already exists (Type: ${detailedType}, State: ${detailedState}). This code may be hidden from the normal view.`);
            } else {
              setModalError(`Code "${finalInviteCode}" already exists (Type: ${detailedType}, State: ${detailedState}).`);
            }
          }).catch(() => {
            // Fallback if search fails
            setModalError(`Code "${finalInviteCode}" already exists. Unable to determine type and state.`);
          });
          
          // Show initial message while searching
          setModalError(`Code "${finalInviteCode}" already exists. Checking details...`);
          
        } else {
          // Other errors
          const errorMsg = typeof errorResponse === 'string' ? 
                          errorResponse : 
                          (errorResponse?.message || 'Failed to create code. Please try again.');
          setModalError(errorMsg);
          console.error('❌ Code creation failed:', errorMsg);
        }
      },
      newCodeData.inviteCode || null, // Custom invite code
      newCodeData.allowMultipleSignup, // Allow multiple signup boolean
      newCodeData.referralGroupObject, // Referral group object
      feelsObject, // Feels object with f_id
      newCodeData.geoRestriction === 'selected_countries' // Convert to boolean for backend
    );
  };

  const handleNewCodeCancel = () => {
    setShowNewCodeModal(false);
    setModalError('');
    setShowAdvancedOptions(false);
    setNewCodeData({
      inviteCode: '',
      inviteLimit: '',
      expirationDate: '',
      geoRestriction: 'worldwide',
      allowMultipleSignup: true,
      referralGroupObject: 'artist',
      feelsId: ''
    });
    setSuggestedCode('');
  };

  const handleNewCodeFieldChange = (field, value) => {
    if (field === 'inviteCode') {
      // Limit to 25 characters, only allow alphanumeric, and force uppercase
      const sanitizedValue = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().substring(0, 25);
      setNewCodeData(prev => ({
        ...prev,
        [field]: sanitizedValue
      }));
    } else {
      setNewCodeData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Invite Limit Modal Handlers
  const handleInviteLimitClick = (index, table) => {
    const codes = table === 'active' ? activeCodes : disabledCodes;
    const code = codes[index];
    
    setInviteLimitModalData({
      index: index,
      table: table,
      currentLimit: code.inviteLimit,
      newLimit: code.inviteLimit.toString()
    });
    setShowInviteLimitModal(true);
  };

  const handleInviteLimitSave = () => {
    const { index, table, newLimit } = inviteLimitModalData;
    const codes = table === 'active' ? activeCodes : disabledCodes;
    const code = codes[index];

    // Validate new limit
    const limitValue = parseInt(newLimit);
    if (isNaN(limitValue) || limitValue < 1) {
      setError('Invite limit must be a number greater than 0');
      return;
    }


    // OPTIMISTIC UI UPDATE: Update limit immediately in the UI
    if (table === 'active') {
      const newActiveCodes = [...activeCodes];
      newActiveCodes[index] = { ...newActiveCodes[index], inviteLimit: limitValue };
      setActiveCodes(newActiveCodes);
      
      // Update combined list
      const combinedCodes = newActiveCodes.concat(disabledCodes);
      setInviteCodes(combinedCodes);
      
      // Update cache too
      const updatedActiveCache = allActiveCodesCache.map(c => 
        c.inviteCode === code.inviteCode ? { ...c, inviteLimit: limitValue } : c
      );
      setAllActiveCodesCache(updatedActiveCache);
      
    } else {
      // For disabled codes, check if this re-enables the code
      const updatedCode = { ...code, inviteLimit: limitValue };
      
      // Check if code should be re-enabled (limit > usage count AND not expired AND not deleted)
      const isExpired = updatedCode.expirationDate !== 'No expiration' && 
                       new Date(updatedCode.expirationDate) <= new Date();
      const isDeleted = updatedCode.activeState === 'Deleted';
      const hasAvailableSpots = limitValue > updatedCode.usageCount;
      
      const shouldReEnable = hasAvailableSpots && !isExpired && !isDeleted;
      
      if (shouldReEnable) {
        // Move from Disabled to Active
        updatedCode.activeState = 'Active';
        updatedCode.disableReason = ''; // Clear disable reason when re-enabling
        
        // Remove from disabled codes
        const newDisabledCodes = disabledCodes.filter((_, i) => i !== index);
        setDisabledCodes(newDisabledCodes);
        
        // Add to active codes at the top
        const newActiveCodes = [updatedCode, ...activeCodes];
        setActiveCodes(newActiveCodes);
        
        // Update combined list
        const combinedCodes = newActiveCodes.concat(newDisabledCodes);
        setInviteCodes(combinedCodes);
        
        // Update cache arrays
        const updatedDisabledCache = allDisabledCodesCache.filter(c => c.inviteCode !== code.inviteCode);
        const updatedActiveCache = [updatedCode, ...allActiveCodesCache];
        
        setAllDisabledCodesCache(updatedDisabledCache);
        setAllActiveCodesCache(updatedActiveCache);
        setDisabledCodesTotal(prev => prev - 1);
        setActiveCodesTotal(prev => prev + 1);
        setDisabledTotalPages(Math.ceil((disabledCodesTotal - 1) / ITEMS_PER_PAGE));
        setActiveTotalPages(Math.ceil((activeCodesTotal + 1) / ITEMS_PER_PAGE));
        
      } else {
        // Just update the limit in disabled codes
        const newDisabledCodes = [...disabledCodes];
        newDisabledCodes[index] = updatedCode;
        setDisabledCodes(newDisabledCodes);
        
        // Update combined list
        const combinedCodes = activeCodes.concat(newDisabledCodes);
        setInviteCodes(combinedCodes);
        
        // Update cache too
        const updatedDisabledCache = allDisabledCodesCache.map(c => 
          c.inviteCode === code.inviteCode ? updatedCode : c
        );
        setAllDisabledCodesCache(updatedDisabledCache);
      }
    }

    // Close modal immediately
    setShowInviteLimitModal(false);
    setInviteLimitModalData({
      index: -1,
      table: '',
      currentLimit: '',
      newLimit: ''
    });

    // Background API call - Don't block UI
    setTimeout(() => {
      
      updateInviteCode(
        code.inviteCode,
        limitValue,
        code.expirationDate,
        false, // not deleted
        null, // is_expired not changed
        (response) => {
          // Don't reload - we already updated the UI optimistically
        },
        (errorMsg) => {
          console.error('❌ Background invite limit update failed:', errorMsg);
          // Could optionally revert the optimistic update here if needed
          // For now, we assume the API will work and keep the optimistic state
        }
      );
    }, 50); // Small delay to ensure UI updates first
  };

  const handleInviteLimitCancel = () => {
    setShowInviteLimitModal(false);
    setInviteLimitModalData({
      index: -1,
      table: '',
      currentLimit: '',
      newLimit: ''
    });
  };

  // Quick Expire Modal Handlers
  const handleQuickExpireClick = (index, table) => {
    const codes = table === 'active' ? activeCodes : disabledCodes;
    const code = codes[index];
    
    setQuickExpireModalData({
      index: index,
      table: table,
      inviteCode: code.inviteCode
    });
    setShowQuickExpireModal(true);
  };

  const handleQuickExpireConfirm = () => {
    const { index, table, inviteCode } = quickExpireModalData;
    const codes = table === 'active' ? activeCodes : disabledCodes;
    const code = codes[index];


    // OPTIMISTIC UI UPDATE: Move code from active to disabled immediately
    if (table === 'active') {
      // Update the code locally to show as expired
      const updatedCode = {
        ...code,
        activeState: 'Expired',
        disableReason: 'Expired', // Set disable reason
        expirationDate: new Date().toLocaleDateString() // Show today's date
      };
      
      // Remove from active codes
      const newActiveCodes = activeCodes.filter((_, i) => i !== index);
      setActiveCodes(newActiveCodes);
      
      // Add to disabled codes
      const newDisabledCodes = [updatedCode, ...disabledCodes];
      setDisabledCodes(newDisabledCodes);
      
      // Update combined list
      const combinedCodes = newActiveCodes.concat(newDisabledCodes);
      setInviteCodes(combinedCodes);
      
      // IMPORTANT: Update cache arrays too for bulk delete to work
      const updatedActiveCache = allActiveCodesCache.filter(c => c.inviteCode !== code.inviteCode);
      // Remove from disabled cache first to prevent duplicates, then add the updated code
      const filteredDisabledCache = allDisabledCodesCache.filter(c => c.inviteCode !== code.inviteCode);
      const updatedDisabledCache = [updatedCode, ...filteredDisabledCache];
      
      // Force synchronous updates to prevent timing issues
      setAllActiveCodesCache(updatedActiveCache);
      setAllDisabledCodesCache(updatedDisabledCache);
      setActiveCodesTotal(prev => prev - 1);
      setDisabledCodesTotal(prev => prev + 1);
      setActiveTotalPages(Math.ceil((activeCodesTotal - 1) / ITEMS_PER_PAGE));
      setDisabledTotalPages(Math.ceil((disabledCodesTotal + 1) / ITEMS_PER_PAGE));
      
      // Force re-render to ensure bulk delete buttons are properly enabled
      // Use a small delay to ensure all state updates have propagated
      setTimeout(() => {
        setAllDisabledCodesCache([...updatedDisabledCache]); // Force re-render
      }, 50);
    }

    // Close modal immediately
    setShowQuickExpireModal(false);
    setQuickExpireModalData({
      index: -1,
      table: '',
      inviteCode: ''
    });

    // Background API call - Don't block UI
    setTimeout(() => {
      const now = new Date();
      const expirationTime = new Date(now.getTime() + 20 * 1000);
      const formattedDate = expirationTime.toISOString();
      
      
      updateInviteCode(
        code.inviteCode,
        parseInt(code.inviteLimit) || 1,
        formattedDate, // Now + 20 seconds
        false, // not deleted
        true, // is_expired = true
        (response) => {
          // Don't reload immediately - let user continue working
          // We could schedule a refresh for later if needed
        },
        (errorMsg) => {
          console.error('❌ Background Quick expire failed:', errorMsg);
          // Could optionally revert the optimistic update here if needed
          // For now, we assume the API will work and keep the optimistic state
        }
      );
    }, 50); // Small delay to ensure UI updates first
  };

  const handleQuickExpireCancel = () => {
    setShowQuickExpireModal(false);
    setQuickExpireModalData({
      index: -1,
      table: '',
      inviteCode: ''
    });
  };

  const handleSaveRow = (index, table) => {
    const codes = table === 'active' ? activeCodes : disabledCodes;
    const code = codes[index];

    // Note: New code creation is now handled in the modal
    // Handle reactivation for disabled codes
    let updatedExpirationDate = code.expirationDate;
    let isDeleted = false;
    
    if (code.actions === 'Reactivate') {
      // Set expiration date to a future date to reactivate
      const futureDate = new Date();
      futureDate.setFullYear(futureDate.getFullYear() + 1); // One year from now
      updatedExpirationDate = futureDate.toLocaleDateString();
    }
    
    // Update existing invite code
    updateInviteCode(
        code.inviteCode,
        parseInt(code.inviteLimit) || 0,
        updatedExpirationDate,
        isDeleted,
        null, // is_expired not changed
        (response) => {
          loadInviteCodesSimple(); // Reload all codes
          setEditingIndex(-1);
          setEditingTable('');
          setOriginalEditData(null);
        },
        (errorMsg) => {
          setError(errorMsg || 'Failed to update invite code');
        },
    );
  };

  const handleEditRow = (index, table) => {
    const codes = table === 'active' ? activeCodes : disabledCodes;
    const codeToEdit = codes[index];
    
    // Store original data before editing
    setOriginalEditData({ ...codeToEdit });
    
    if (table === 'active') {
      const updatedCodes = [...activeCodes];
      updatedCodes[index].status = 'Edit';
      setActiveCodes(updatedCodes);
    } else {
      const updatedCodes = [...disabledCodes];
      updatedCodes[index].status = 'Edit';
      setDisabledCodes(updatedCodes);
    }
    setEditingIndex(index);
    setEditingTable(table);
  };

  const handleDeleteRow = (index, table) => {
    const codes = table === 'active' ? activeCodes : disabledCodes;
    const code = codes[index];

    if (code.isNew) {
      // Just remove from local state if it's a new unsaved code
      if (table === 'active') {
        const updatedCodes = activeCodes.filter((_, i) => i !== index);
        setActiveCodes(updatedCodes);
      } else {
        const updatedCodes = disabledCodes.filter((_, i) => i !== index);
        setDisabledCodes(updatedCodes);
      }
      if (editingIndex === index && editingTable === table) {
        setEditingIndex(-1);
        setEditingTable('');
      }
    } else {
      // Delete from API using soft delete (set is_deleted = true)
      // Since soft delete isn't working, use hard delete and let UI manage the state
      deleteInviteCode(
          code.inviteCode,
          (response) => {
            // Move code to deleted table for UI purposes
            const deletedCode = { ...code, activeState: 'Deleted', disableReason: 'Deleted' };
            
            // Remove from current table cache
            const updatedActiveCache = allActiveCodesCache.filter(c => c.inviteCode !== code.inviteCode);
            const updatedDisabledCache = allDisabledCodesCache.filter(c => c.inviteCode !== code.inviteCode);
            
            // Add to deleted cache (remove any existing duplicate first)
            const filteredDeletedCache = allDeletedCodesCache.filter(c => c.inviteCode !== code.inviteCode);
            const updatedDeletedCache = [deletedCode, ...filteredDeletedCache];
            
            setAllActiveCodesCache(updatedActiveCache);
            setAllDisabledCodesCache(updatedDisabledCache);
            setAllDeletedCodesCache(updatedDeletedCache);
            setDeletedCodesTotal(prev => prev + 1);
            setDeletedTotalPages(Math.ceil((deletedCodesTotal + 1) / ITEMS_PER_PAGE));
            
            if (table === 'active') {
              setActiveCodesTotal(prev => prev - 1);
              setActiveTotalPages(Math.ceil((activeCodesTotal - 1) / ITEMS_PER_PAGE));
              loadActivePageFromCache(activeCurrentPage, updatedActiveCache);
            } else {
              setDisabledCodesTotal(prev => prev - 1);
              setDisabledTotalPages(Math.ceil((disabledCodesTotal - 1) / ITEMS_PER_PAGE));
              loadDisabledPageFromCache(disabledCurrentPage, updatedDisabledCache);
            }
            
            // If on first page of deleted table, show the new deleted code
            if (deletedCurrentPage === 1) {
              loadDeletedPageFromCache(1, updatedDeletedCache);
            }
            
            setEditingIndex(-1);
            setEditingTable('');
            setOriginalEditData(null);
            
            // Don't reload from backend since we hard-deleted the codes
          },
          (errorMsg) => {
            console.error(`❌ Hard delete API failed for ${code.inviteCode}:`, errorMsg);
            setError(errorMsg || 'Failed to delete invite code');
          }
      );
    }
  };

  const handleCancelRow = (index, table) => {
    if (table === 'active') {
      const updatedCodes = activeCodes.filter((_, i) => i !== index);
      setActiveCodes(updatedCodes);
    } else {
      const updatedCodes = disabledCodes.filter((_, i) => i !== index);
      setDisabledCodes(updatedCodes);
    }
    setEditingIndex(-1);
    setEditingTable('');
  };

  const handleCancelEditRow = (index, table) => {
    // Revert to original values stored when editing started
    if (originalEditData) {
      if (table === 'active') {
        const updatedCodes = [...activeCodes];
        updatedCodes[index] = { ...originalEditData };
        setActiveCodes(updatedCodes);
      } else {
        const updatedCodes = [...disabledCodes];
        updatedCodes[index] = { ...originalEditData };
        setDisabledCodes(updatedCodes);
      }
    }
    
    setEditingIndex(-1);
    setEditingTable('');
    setOriginalEditData(null);
  };

  const handleCellChange = (index, field, value, table) => {
    if (table === 'active') {
      const updatedCodes = [...activeCodes];
      updatedCodes[index][field] = value;
      setActiveCodes(updatedCodes);
    } else {
      const updatedCodes = [...disabledCodes];
      updatedCodes[index][field] = value;
      setDisabledCodes(updatedCodes);
    }
    
    // Note: Active State is now immutable and calculated, no manual table movement needed
  };

  const handleMoveExpiration = (index, table) => {
    setDatePickerIndex(index);
    setDatePickerTable(table);
    setShowDatePicker(true);
    
    // Set current expiration date as default
    const codes = table === 'active' ? activeCodes : disabledCodes;
    const code = codes[index];
    
    // Convert current date to YYYY-MM-DD format for date input
    let defaultDate = '';
    if (code.expirationDate && code.expirationDate !== 'No expiration') {
      try {
        const date = new Date(code.expirationDate);
        if (!isNaN(date.getTime())) {
          defaultDate = date.toISOString().split('T')[0];
        }
      } catch (e) {
        console.warn('Failed to parse current expiration date:', e);
      }
    }
    
    setSelectedDate(defaultDate);
  };

  const handleDateSelection = (newDate) => {
    if (datePickerIndex >= 0 && datePickerTable && newDate) {
      const codes = datePickerTable === 'active' ? activeCodes : disabledCodes;
      const code = codes[datePickerIndex];
      
      
      // Convert date to display format - avoid timezone issues
      let displayDate;
      if (newDate === 'No expiration') {
        displayDate = 'No expiration';
      } else {
        // Parse the date as local time to avoid timezone conversion issues
        const dateParts = newDate.split('-');
        if (dateParts.length === 3) {
          const year = parseInt(dateParts[0]);
          const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
          const day = parseInt(dateParts[2]);
          displayDate = new Date(year, month, day).toLocaleDateString();
        } else {
          // Fallback for other date formats
          displayDate = new Date(newDate).toLocaleDateString();
        }
      }
      
      // OPTIMISTIC UI UPDATE: Update expiration date immediately in the UI
      if (datePickerTable === 'active') {
        const newActiveCodes = [...activeCodes];
        newActiveCodes[datePickerIndex] = { ...newActiveCodes[datePickerIndex], expirationDate: displayDate };
        setActiveCodes(newActiveCodes);
        
        // Update combined list
        const combinedCodes = newActiveCodes.concat(disabledCodes);
        setInviteCodes(combinedCodes);
        
      } else {
        // For disabled codes, check if this re-enables the code
        const updatedCode = { ...code, expirationDate: displayDate };
        
        // Check if code should be re-enabled (future date AND limit > usage count AND not deleted)
        const isFutureDate = displayDate === 'No expiration' || new Date(newDate) > new Date();
        const isDeleted = updatedCode.activeState === 'Deleted';
        const hasAvailableSpots = updatedCode.inviteLimit > updatedCode.usageCount;
        
        const shouldReEnable = isFutureDate && hasAvailableSpots && !isDeleted;
        
        if (shouldReEnable) {
          // Move from Disabled to Active
          updatedCode.activeState = 'Active';
          
          // Remove from disabled codes
          const newDisabledCodes = disabledCodes.filter((_, i) => i !== datePickerIndex);
          setDisabledCodes(newDisabledCodes);
          
          // Add to active codes at the top
          const newActiveCodes = [updatedCode, ...activeCodes];
          setActiveCodes(newActiveCodes);
          
          // Update combined list
          const combinedCodes = newActiveCodes.concat(newDisabledCodes);
          setInviteCodes(combinedCodes);
          
        } else {
          // Just update the expiration date in disabled codes
          // Also need to recalculate disable reason in case it changed
          const now = new Date();
          const expirationDateObj = displayDate === 'No expiration' ? null : new Date(newDate);
          const isExpired = expirationDateObj && expirationDateObj <= now;
          const hasReachedMaxSignups = updatedCode.inviteLimit > 0 && updatedCode.usageCount >= updatedCode.inviteLimit;
          
          // Update disable reason based on new expiration
          if (isExpired) {
            updatedCode.disableReason = 'Expired';
          } else if (hasReachedMaxSignups) {
            updatedCode.disableReason = 'Spots Filled';
          }
          
          const newDisabledCodes = [...disabledCodes];
          newDisabledCodes[datePickerIndex] = updatedCode;
          setDisabledCodes(newDisabledCodes);
          
          // Update combined list
          const combinedCodes = activeCodes.concat(newDisabledCodes);
          setInviteCodes(combinedCodes);
          
        }
      }
      
      // Close the date picker immediately
      setShowDatePicker(false);
      setDatePickerIndex(-1);
      setDatePickerTable('');
      setSelectedDate('');
      
      // Background API call - Don't block UI
      setTimeout(() => {
        // Convert date to proper format for API - avoid timezone conversion
        let formattedDate = newDate;
        if (newDate !== 'No expiration' && !newDate.includes('T')) {
          // Parse as local time and convert to ISO string for API
          const dateParts = newDate.split('-');
          if (dateParts.length === 3) {
            const year = parseInt(dateParts[0]);
            const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
            const day = parseInt(dateParts[2]);
            // Create as local time, then convert to ISO for API
            formattedDate = new Date(year, month, day).toISOString();
          } else {
            formattedDate = newDate + 'T00:00:00.000Z'; // Fallback
          }
        }
        
        
        const validInviteLimit = parseInt(code.inviteLimit) || 1;
        updateInviteCode(
          code.inviteCode,
          validInviteLimit,
          formattedDate, // Send formatted date to API
          false, // not deleted
          null, // is_expired not changed
          (response) => {
            // Don't reload - we already updated the UI optimistically
          },
          (errorMsg) => {
            console.error('❌ Background expiration date update failed:', errorMsg);
            // Could optionally revert the optimistic update here if needed
            // For now, we assume the API will work and keep the optimistic state
          }
        );
      }, 50); // Small delay to ensure UI updates first
    }
  };

  const handleCloseDatePicker = () => {
    setShowDatePicker(false);
    setDatePickerIndex(-1);
    setDatePickerTable('');
    setSelectedDate('');
  };

  const handleGeoRestrictionClick = (index, table) => {
    const codes = table === 'active' ? activeCodes : disabledCodes;
    const code = codes[index];
    
    // Show modal instead of immediate toggle
    setGeoRestrictionModalData({
      index: index,
      table: table,
      currentGeoRestriction: code.geoRestriction,
      newGeoRestriction: code.geoRestriction
    });
    setShowGeoRestrictionModal(true);
  };

  const handleGeoRestrictionModalSubmit = () => {
    const { index, table, newGeoRestriction } = geoRestrictionModalData;
    const codes = table === 'active' ? activeCodes : disabledCodes;
    const code = codes[index];
    
    // OPTIMISTIC UI UPDATE: Update geo restriction immediately in the UI
    if (table === 'active') {
      const newActiveCodes = [...activeCodes];
      newActiveCodes[index] = { ...newActiveCodes[index], geoRestriction: newGeoRestriction };
      setActiveCodes(newActiveCodes);
      
      // Update combined list
      const combinedCodes = newActiveCodes.concat(disabledCodes);
      setInviteCodes(combinedCodes);
      
    } else {
      const newDisabledCodes = [...disabledCodes];
      newDisabledCodes[index] = { ...newDisabledCodes[index], geoRestriction: newGeoRestriction };
      setDisabledCodes(newDisabledCodes);
      
      // Update combined list
      const combinedCodes = activeCodes.concat(newDisabledCodes);
      setInviteCodes(combinedCodes);
    }
    
    // Close modal
    setShowGeoRestrictionModal(false);
    
    // Update backend with small delay to ensure UI updates first
    setTimeout(() => {
      const geoRestrictedBoolean = newGeoRestriction === 'selected_countries';
      
      updateInviteCode(
        code.inviteCode,
        parseInt(code.inviteLimit) || 1,
        code.expirationDate,
        false, // not deleted
        null, // is_expired not changed
        (response) => {
          // Success - optimistic update is already applied
          console.log('✅ Geo restriction updated successfully');
        },
        (errorMsg) => {
          console.error('❌ Geo restriction update failed:', errorMsg);
          // Could revert the optimistic update here if needed
        },
        geoRestrictedBoolean // Add geo_restricted parameter
      );
    }, 50);
  };

  const handleGeoRestrictionModalCancel = () => {
    setShowGeoRestrictionModal(false);
    setGeoRestrictionModalData({
      index: -1,
      table: '',
      currentGeoRestriction: 'worldwide',
      newGeoRestriction: 'worldwide'
    });
  };

  const handleExtLinkMouseEnter = () => {
    const timer = setTimeout(() => {
      setShowExtLinkTooltip(true);
    }, 1000); // Show after 1 second
    setExtLinkTooltipTimer(timer);
  };

  const handleExtLinkMouseLeave = () => {
    if (extLinkTooltipTimer) {
      clearTimeout(extLinkTooltipTimer);
      setExtLinkTooltipTimer(null);
    }
    setShowExtLinkTooltip(false);
  };

  // Pagination Component
  // eslint-disable-next-line react/prop-types
  const PaginationControls = ({ currentPage, totalPages, onPageChange, loading, totalItems, itemsPerPage = ITEMS_PER_PAGE, backgroundLoading }) => {
    const renderPageNumbers = () => {
      const pages = [];
      const maxPagesToShow = 5;
      
      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      
      if (endPage - startPage < maxPagesToShow - 1) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
      
      if (startPage > 1) {
        pages.push(
          <button
            key="1"
            onClick={() => onPageChange(1)}
            className={styles.paginationButton}
            disabled={loading}
          >
            1
          </button>
        );
        if (startPage > 2) {
          pages.push(<span key="dots1" className={styles.paginationDots}>...</span>);
        }
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => onPageChange(i)}
            className={`${styles.paginationButton} ${currentPage === i ? styles.paginationActive : ''}`}
            disabled={loading || currentPage === i}
          >
            {i}
          </button>
        );
      }
      
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push(<span key="dots2" className={styles.paginationDots}>...</span>);
        }
        pages.push(
          <button
            key={totalPages}
            onClick={() => onPageChange(totalPages)}
            className={styles.paginationButton}
            disabled={loading}
          >
            {totalPages}
          </button>
        );
      }
      
      return pages;
    };
    
    return (
      <div className={styles.paginationContainer}>
        <div className={styles.paginationInfo}>
          {totalItems > 0 ? (
            <>
              Showing {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} of{' '}
              {backgroundLoading ? (
                <span style={{ color: '#ff006e', fontWeight: '600' }}>
                  ... calculating
                </span>
              ) : (
                <>{totalItems} items</>
              )}
            </>
          ) : (
            <>No items to display</>
          )}
        </div>
        <div className={styles.paginationControls}>
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            className={styles.paginationButton}
          >
            Previous
          </button>
          {renderPageNumbers()}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages || loading}
            className={styles.paginationButton}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Manage Access Modal Functions
  const handleOpenManageAccess = async () => {
    const users = await getAllowlist();
    setAllowlistUsers(users);
    setShowManageAccessModal(true);
  };

  const handleCloseManageAccess = () => {
    setShowManageAccessModal(false);
    setNewUserAlias('');
  };

  const handleAddUser = async () => {
    if (newUserAlias.trim()) {
      // Prevent adding email domains by checking for @ symbol
      const cleanAlias = newUserAlias.trim();
      if (cleanAlias.includes('@')) {
        alert('Please enter only the username, not the full email address (e.g., "john" not "john@company.com")');
        return;
      }
      
      const success = await addToAllowlist(cleanAlias);
      if (success) {
        const updatedUsers = await getAllowlist();
        setAllowlistUsers(updatedUsers);
        setNewUserAlias('');
      }
    }
  };

  const handleRemoveUser = async (alias) => {
    const success = await removeFromAllowlist(alias);
    if (success) {
      const updatedUsers = await getAllowlist();
      setAllowlistUsers(updatedUsers);
    }
  };

  // Bulk Delete Functions
  const handleBulkDelete = () => {
    // Get codes to delete using invite codes from the cache (not just current page)
    const selectedInviteCodes = Array.from(selectedCodesForDelete);
    const codesToDelete = allDisabledCodesCache.filter(code => selectedInviteCodes.includes(code.inviteCode));
    let completedDeletes = 0;
    let totalDeletes = codesToDelete.length;
    
    // Function to handle completion of all deletes
    const handleAllDeletesComplete = () => {
      // Get the codes that were deleted using invite codes
      const deletedCodeIds = new Set(selectedInviteCodes);
      
      // Move codes to deleted table
      const deletedCodesWithState = codesToDelete.map(code => ({ ...code, activeState: 'Deleted', disableReason: 'Deleted' }));
      
      // Update caches by moving deleted codes
      const updatedDisabledCache = allDisabledCodesCache.filter(code => !deletedCodeIds.has(code.inviteCode));
      const updatedActiveCache = allActiveCodesCache.filter(code => !deletedCodeIds.has(code.inviteCode));
      // Remove any existing duplicates from deleted cache first
      const filteredDeletedCache = allDeletedCodesCache.filter(code => !deletedCodeIds.has(code.inviteCode));
      const updatedDeletedCache = [...deletedCodesWithState, ...filteredDeletedCache];
      
      setAllDisabledCodesCache(updatedDisabledCache);
      setAllActiveCodesCache(updatedActiveCache);
      setAllDeletedCodesCache(updatedDeletedCache);
      setDisabledCodesTotal(prev => prev - codesToDelete.length);
      setDeletedCodesTotal(prev => prev + codesToDelete.length);
      setDisabledTotalPages(Math.ceil((disabledCodesTotal - codesToDelete.length) / ITEMS_PER_PAGE));
      setDeletedTotalPages(Math.ceil((deletedCodesTotal + codesToDelete.length) / ITEMS_PER_PAGE));
      
      // Reload current page from updated cache
      loadDisabledPageFromCache(disabledCurrentPage, updatedDisabledCache);
      
      // If on first page of deleted table, show the new deleted codes
      if (deletedCurrentPage === 1) {
        loadDeletedPageFromCache(1, updatedDeletedCache);
      }
      
      // Reset bulk delete state
      setSelectedCodesForDelete(new Set());
      setBulkDeleteMode(false);
      setShowBulkDeleteConfirm(false);
    };
    
    // Call API to hard delete each selected code (soft delete not working)
    codesToDelete.forEach(code => {
      deleteInviteCode(
        code.inviteCode, // token
        () => {
          // Success callback
          completedDeletes++;
          if (completedDeletes === totalDeletes) {
            handleAllDeletesComplete();
          }
        },
        (error) => {
          // Failure callback
          console.error(`❌ Failed to hard delete code ${code.inviteCode}:`, error);
          completedDeletes++;
          if (completedDeletes === totalDeletes) {
            handleAllDeletesComplete();
          }
        }
      );
    });
  };

  const handleClearExpiration = (index, table) => {
    const codes = table === 'active' ? activeCodes : disabledCodes;
    const code = codes[index];
    
    
    // OPTIMISTIC UI UPDATE: Clear expiration date immediately in the UI
    if (table === 'active') {
      const newActiveCodes = [...activeCodes];
      newActiveCodes[index] = { ...newActiveCodes[index], expirationDate: 'No expiration' };
      setActiveCodes(newActiveCodes);
      
      // Update combined list
      const combinedCodes = newActiveCodes.concat(disabledCodes);
      setInviteCodes(combinedCodes);
      
    } else {
      // For disabled codes, check if this re-enables the code
      const updatedCode = { ...code, expirationDate: 'No expiration' };
      
      // Check if code should be re-enabled (no expiration AND limit > usage count AND not deleted)
      const isDeleted = updatedCode.activeState === 'Deleted';
      const hasAvailableSpots = updatedCode.inviteLimit > updatedCode.usageCount;
      
      const shouldReEnable = hasAvailableSpots && !isDeleted;
      
      if (shouldReEnable) {
        // Move from Disabled to Active
        updatedCode.activeState = 'Active';
        updatedCode.disableReason = ''; // Clear disable reason when re-enabling
        
        // Remove from disabled codes
        const newDisabledCodes = disabledCodes.filter((_, i) => i !== index);
        setDisabledCodes(newDisabledCodes);
        
        // Add to active codes at the top
        const newActiveCodes = [updatedCode, ...activeCodes];
        setActiveCodes(newActiveCodes);
        
        // Update combined list
        const combinedCodes = newActiveCodes.concat(newDisabledCodes);
        setInviteCodes(combinedCodes);
        
      } else {
        // Just clear the expiration date in disabled codes
        const newDisabledCodes = [...disabledCodes];
        newDisabledCodes[index] = updatedCode;
        setDisabledCodes(newDisabledCodes);
        
        // Update combined list
        const combinedCodes = activeCodes.concat(newDisabledCodes);
        setInviteCodes(combinedCodes);
        
      }
    }
    
    // Background API call - Don't block UI
    setTimeout(() => {
      
      updateInviteCode(
        code.inviteCode,
        parseInt(code.inviteLimit) || 1,
        null, // Set to null to remove expiration
        false, // not deleted
        null, // is_expired not changed
        (response) => {
          // Don't reload - we already updated the UI optimistically
        },
        (errorMsg) => {
          console.error('❌ Background clear expiration failed:', errorMsg);
          // Could optionally revert the optimistic update here if needed
          // For now, we assume the API will work and keep the optimistic state
        }
      );
    }, 50); // Small delay to ensure UI updates first
  };


  const finishEditing = () => {
    // Finish editing
    if (editingIndex >= 0 && editingTable) {
      setEditingIndex(-1);
      setEditingTable('');
    }
  };

  const renderTable = (codes, tableType, title) => {
    // Get the appropriate ref based on table type
    const getTableRef = () => {
      switch (tableType) {
        case 'active': return activeTableRef;
        case 'disabled': return disabledTableRef;
        case 'deleted': return deletedTableRef;
        default: return null;
      }
    };

    return (
    <div ref={getTableRef()} className={styles.tableSection}>
      <div className={styles.tableHeader}>
        <div className={styles.tableHeaderLeft}>
          <h2 className={styles.tableTitle}>
            {title} ({tableType === 'active' ? activeCodesTotal : tableType === 'disabled' ? disabledCodesTotal : deletedCodesTotal})
            {backgroundLoading && (
              <span style={{ 
                fontSize: '14px', 
                fontWeight: '600',
                marginLeft: '16px',
                color: '#ff006e',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Loading more data...
              </span>
            )}
            {tableType === 'deleted' && (
              <div style={{ fontSize: '12px', color: '#999', fontWeight: 'normal', marginTop: '4px' }}>
                Deleted codes will disappear in 24-48 hours
              </div>
            )}
          </h2>
        </div>
        {tableType === 'active' && (
          <div className={styles.tableHeaderRight}>
            <button
              className={styles.newCodeButton}
              onClick={addNewCode}
              style={{ marginBottom: '0px' }}
            >
              New Code
            </button>
            <button
              onClick={handleOpenManageAccess}
              className={styles.manageAccessLink}
            >
              Manage Access
            </button>
          </div>
        )}
      </div>
      {tableType === 'disabled' && (
        <div style={{ 
          padding: '16px 0', 
          textAlign: 'center',
          borderBottom: '1px solid #333',
          marginBottom: '16px',
          minHeight: '70px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          opacity: bulkDeleteMode ? 1 : 0,
          visibility: bulkDeleteMode ? 'visible' : 'hidden',
          transition: 'opacity 0.2s ease'
        }}>
          <button
            onClick={() => setShowBulkDeleteConfirm(true)}
            disabled={selectedCodesForDelete.size === 0}
            style={{
              backgroundColor: selectedCodesForDelete.size > 0 ? '#ff006e' : '#666',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: selectedCodesForDelete.size > 0 ? 'pointer' : 'not-allowed',
              fontWeight: '600',
              fontSize: '14px',
              opacity: selectedCodesForDelete.size > 0 ? 1 : 0.5,
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              if (selectedCodesForDelete.size > 0) {
                e.target.style.backgroundColor = '#e6005d';
              }
            }}
            onMouseOut={(e) => {
              if (selectedCodesForDelete.size > 0) {
                e.target.style.backgroundColor = '#ff006e';
              }
            }}
          >
            Delete Selected ({selectedCodesForDelete.size})
          </button>
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
            Select codes below to delete them permanently
          </div>
        </div>
      )}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              {tableType === 'disabled' && bulkDeleteMode && (
                <th style={{ width: '40px', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCodesForDelete(new Set(codes.map(code => code.inviteCode)));
                      } else {
                        setSelectedCodesForDelete(new Set());
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
              )}
              <th
                className={styles.sortableHeader}
                onClick={() => handleSort('inviteCode', tableType)}
                title="Invite Code"
              >
                {tableType === 'disabled' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent column sorting
                      setBulkDeleteMode(!bulkDeleteMode);
                      if (bulkDeleteMode) {
                        // Clear selections when exiting bulk delete mode
                        setSelectedCodesForDelete(new Set());
                      }
                    }}
                    style={{
                      background: '#000000',
                      border: bulkDeleteMode 
                        ? '2px solid #ff006e' 
                        : '2px solid rgba(255, 0, 110, 0.5)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      padding: '6px 8px',
                      marginRight: '12px',
                      color: bulkDeleteMode ? '#ff006e' : '#ffffff',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      boxShadow: bulkDeleteMode 
                        ? '0 0 15px rgba(255, 0, 110, 0.8), 0 0 25px rgba(139, 0, 255, 0.6), 0 0 35px rgba(0, 212, 255, 0.4)'
                        : '0 0 8px rgba(255, 0, 110, 0.4), 0 0 12px rgba(139, 0, 255, 0.3)'
                    }}
                    title={bulkDeleteMode ? 'Cancel bulk delete' : 'Bulk delete codes'}
                    onMouseOver={(e) => {
                      e.target.style.transform = 'scale(1.1)';
                      e.target.style.boxShadow = '0 0 30px rgba(255, 0, 110, 0.8), 0 0 50px rgba(139, 0, 255, 0.6), 0 0 70px rgba(0, 212, 255, 0.5)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = bulkDeleteMode 
                        ? '0 0 20px rgba(255, 0, 110, 0.6), 0 0 40px rgba(139, 0, 255, 0.4), 0 0 60px rgba(0, 212, 255, 0.3)'
                        : '0 0 10px rgba(255, 0, 110, 0.3), 0 0 20px rgba(139, 0, 255, 0.2)';
                    }}
                  >
                    🗑️
                  </button>
                )}
                Invite Code{getSortIcon('inviteCode')}
              </th>
              <th
                className={`${styles.sortableHeader} ${styles.dateColumn}`}
                onClick={() => handleSort('dateCreated', tableType)}
                title="Create Date"
              >
                Create Date{getSortIcon('dateCreated')}
              </th>
              <th
                className={`${styles.sortableHeader} ${styles.dateColumn}`}
                onClick={() => handleSort('expirationDate', tableType)}
                title="Expiration Date"
              >
                Expiration Date{getSortIcon('expirationDate')}
              </th>
              <th
                className={styles.sortableHeader}
                onClick={() => handleSort('inviteLimit', tableType)}
                title="Invite Limit"
              >
                Invite Limit{getSortIcon('inviteLimit')}
              </th>
              <th
                className={styles.sortableHeader}
                onClick={() => handleSort('usageCount', tableType)}
                title="Usage Count"
              >
                Usage Count{getSortIcon('usageCount')}
              </th>
              {(tableType === 'disabled' || tableType === 'deleted') && (
                <th
                  className={`${styles.sortableHeader} ${styles.referralGroupObjectColumn}`}
                  onClick={() => handleSort('referralGroupObject', tableType)}
                  title="Type"
                >
                  Type{getSortIcon('referralGroupObject')}
                </th>
              )}
              {(tableType === 'disabled' || tableType === 'deleted') && (
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSort('geoRestriction', tableType)}
                  title="REDEEMABLE COUNTRIES"
                >
                  REDEEMABLE COUNTRIES{getSortIcon('geoRestriction')}
                </th>
              )}
              {(tableType === 'disabled' || tableType === 'deleted') && (
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSort('fastFeelsUrl', tableType)}
                  title="FAST FEELS"
                >
                  FAST FEELS{getSortIcon('fastFeelsUrl')}
                </th>
              )}
              {(tableType === 'disabled' || tableType === 'deleted') && (
                <th
                  className={`${styles.sortableHeader} ${styles.referralGroupIdColumn}`}
                  onClick={() => handleSort('creator', tableType)}
                  title="CREATOR"
                >
                  CREATOR{getSortIcon('creator')}
                </th>
              )}
              {tableType === 'disabled' && (
                <th
                  className={`${styles.sortableHeader} ${styles.narrowColumn}`}
                  onClick={() => handleSort('disableReason', tableType)}
                  title="Disable Reason"
                >
                  Disable Reason{getSortIcon('disableReason')}
                </th>
              )}
              {tableType === 'active' && (
                <>
                  <th
                    className={`${styles.sortableHeader} ${styles.narrowColumn}`}
                    onClick={() => handleSort('activeState', tableType)}
                    title="Active State"
                  >
                    Active State{getSortIcon('activeState')}
                  </th>
                  <th
                    className={styles.sortableHeader}
                    onClick={() => handleSort('actions', tableType)}
                    title="Deactivate"
                  >
                    Deactivate{getSortIcon('actions')}
                  </th>
                </>
              )}
              {/* <th
                className={styles.sortableHeader}
                onClick={() => handleSort('notes', tableType)}
              >
                Notes{getSortIcon('notes')}
              </th> */}
              {tableType === 'active' && (
                <>
                  <th
                    className={styles.sortableHeader}
                    onClick={() => handleSort('songArtistName', tableType)}
                    title="External Link"
                  >
                    Ext. Link
                    <span 
                      style={{ 
                        marginLeft: '4px', 
                        cursor: 'help',
                        color: '#999',
                        fontSize: '12px',
                        fontWeight: 'normal',
                        position: 'relative',
                        display: 'inline-block'
                      }}
                      onMouseEnter={handleExtLinkMouseEnter}
                      onMouseLeave={handleExtLinkMouseLeave}
                    >
                      ?
                      {showExtLinkTooltip && (
                        <div 
                          style={{
                            position: 'absolute',
                            top: '20px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(0, 0, 0, 0.9)',
                            color: '#fff',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                          }}
                        >
                          External link to Feels
                          <div 
                            style={{
                              position: 'absolute',
                              top: '-5px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: 0,
                              height: 0,
                              borderLeft: '5px solid transparent',
                              borderRight: '5px solid transparent',
                              borderBottom: '5px solid rgba(0, 0, 0, 0.9)'
                            }}
                          />
                        </div>
                      )}
                    </span>
                    {getSortIcon('songArtistName')}
                  </th>
                  <th
                    className={styles.sortableHeader}
                    onClick={() => handleSort('fastFeelsUrl', tableType)}
                  >
                    FAST FEELS{getSortIcon('fastFeelsUrl')}
                  </th>
                  <th
                    className={styles.sortableHeader}
                    onClick={() => handleSort('multiSignup', tableType)}
                  >
                    Multi{getSortIcon('multiSignup')}
                  </th>
                </>
              )}
              {tableType === 'active' && (
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSort('geoRestriction', tableType)}
                  title="REDEEMABLE COUNTRIES"
                >
                  REDEEMABLE COUNTRIES{getSortIcon('geoRestriction')}
                </th>
              )}
              {tableType === 'active' && (
                <th
                  className={`${styles.sortableHeader} ${styles.referralGroupObjectColumn}`}
                  onClick={() => handleSort('referralGroupObject', tableType)}
                >
                  TYPE{getSortIcon('referralGroupObject')}
                </th>
              )}
              {tableType === 'active' && (
                <th
                  className={`${styles.sortableHeader} ${styles.referralGroupIdColumn}`}
                  onClick={() => handleSort('creator', tableType)}
                >
                  Creator{getSortIcon('creator')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {codes.map((code, index) => (
              <tr key={`${tableType}-${index}`} className={styles.tableRow}>
                {tableType === 'disabled' && bulkDeleteMode && (
                  <td style={{ textAlign: 'center', width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedCodesForDelete.has(code.inviteCode)}
                      onChange={(e) => {
                        const newSelected = new Set(selectedCodesForDelete);
                        if (e.target.checked) {
                          newSelected.add(code.inviteCode);
                        } else {
                          newSelected.delete(code.inviteCode);
                        }
                        setSelectedCodesForDelete(newSelected);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                )}
                <td>
                  {editingIndex === index && editingTable === tableType && code.isNew && tableType !== 'deleted' ? (
                  <input
                    type="text"
                    value={code.inviteCode}
                    onChange={(e) => handleCellChange(index, 'inviteCode', e.target.value, tableType)}
                    onBlur={finishEditing}
                    className={styles.inputField}
                    maxLength="20"
                    autoFocus
                  />
                ) : (
                  code.inviteCode
                )}
                </td>
                <td className={styles.dateColumn}>
                  {code.dateCreated}
                </td>
                <td className={styles.dateColumn}>
                  {tableType === 'deleted' ? (
                    code.expirationDate
                  ) : (
                    <span 
                      onClick={() => handleMoveExpiration(index, tableType)}
                      style={{ 
                        cursor: 'pointer', 
                        color: '#ff006e',
                        textDecoration: 'underline',
                        fontWeight: '600'
                      }}
                      title="Click to edit expiration date"
                    >
                      {code.expirationDate}
                    </span>
                  )}
                </td>
                <td>
                  {tableType === 'deleted' ? (
                    code.inviteLimit
                  ) : (
                    <span 
                      onClick={() => handleInviteLimitClick(index, tableType)}
                      style={{ 
                        cursor: 'pointer', 
                        color: '#ff006e',
                        textDecoration: 'underline',
                        fontWeight: '600'
                      }}
                      title="Click to edit invite limit"
                    >
                      {code.inviteLimit}
                    </span>
                  )}
                </td>
                <td>
                  {code.usageCount}
                </td>
                {(tableType === 'disabled' || tableType === 'deleted') && (
                  <td className={styles.referralGroupObjectColumn}>
                    {code.referralGroupObject}
                  </td>
                )}
                {(tableType === 'disabled' || tableType === 'deleted') && (
                  <td>
                    {tableType === 'deleted' ? (
                      <span style={{ color: '#b8b8b8' }}>
                        {code.geoRestriction === 'worldwide' ? 'Worldwide' : 'Allow Listed Countries'}
                      </span>
                    ) : (
                      <span 
                        onClick={() => handleGeoRestrictionClick(index, tableType)}
                        style={{ 
                          cursor: 'pointer', 
                          color: '#ff006e',
                          textDecoration: 'underline',
                          fontWeight: '600'
                        }}
                        title="Click to toggle geo restriction"
                      >
                        {code.geoRestriction === 'worldwide' ? 'Worldwide' : 'Allow Listed Countries'}
                      </span>
                    )}
                  </td>
                )}
                {(tableType === 'disabled' || tableType === 'deleted') && (
                  <td>
                    {code.fastFeelsUrl ? (
                      <a 
                        href={code.fastFeelsUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#ff006e', textDecoration: 'none' }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        {code.songArtistName}
                      </a>
                    ) : (
                      <span style={{ color: '#666' }}>{code.songArtistName}</span>
                    )}
                  </td>
                )}
                {(tableType === 'disabled' || tableType === 'deleted') && (
                  <td>
                    {code.creator}
                  </td>
                )}
                {tableType === 'disabled' && (
                  <td className={styles.narrowColumn}>
                    {code.disableReason}
                  </td>
                )}
                {tableType === 'active' && (
                  <>
                    <td className={styles.narrowColumn}>
                      {code.activeState}
                    </td>
                    <td>
                      <button
                        className={`${styles.button} ${styles.moveExpirationButton}`}
                        onClick={() => handleQuickExpireClick(index, tableType)}
                      >
                        Quick Expire
                      </button>
                    </td>
                  </>
                )}
                {/* <td>
                  {editingIndex === index && editingTable === tableType ? (
                  <input
                    type="text"
                    value={code.notes}
                    onChange={(e) => handleCellChange(index, 'notes', e.target.value, tableType)}
                    onBlur={finishEditing}
                    className={styles.inputField}
                  />
                ) : (
                  code.notes
                )}
                </td> */}
                {tableType === 'active' && (
                  <>
                    <td>
                      {code.videoUrl720x1280 ? (
                        <a 
                          href={code.videoUrl720x1280} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#ff006e', textDecoration: 'none' }}
                          onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                        >
                          {code.songArtistName}
                        </a>
                      ) : (
                        <span style={{ color: '#666' }}>{code.songArtistName}</span>
                      )}
                    </td>
                    <td>
                      {code.fastFeelsUrl ? (
                        <a 
                          href={code.fastFeelsUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#ff006e', textDecoration: 'none' }}
                          onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                        >
                          {code.songArtistName}
                        </a>
                      ) : (
                        <span style={{ color: '#666' }}>{code.songArtistName}</span>
                      )}
                    </td>
                    <td>
                      {code.multiSignup}
                    </td>
                    <td>
                      <span 
                        onClick={() => handleGeoRestrictionClick(index, tableType)}
                        style={{ 
                          cursor: 'pointer', 
                          color: '#ff006e',
                          textDecoration: 'underline',
                          fontWeight: '600'
                        }}
                        title="Click to toggle geo restriction"
                      >
                        {code.geoRestriction === 'worldwide' ? 'Worldwide' : 'Allow Listed Countries'}
                      </span>
                    </td>
                    <td className={styles.referralGroupObjectColumn}>
                      {code.referralGroupObject}
                    </td>
                    <td>
                      {code.creator}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Add pagination controls */}
      {tableType === 'active' && (
        <PaginationControls
          currentPage={activeCurrentPage}
          totalPages={activeTotalPages}
          onPageChange={handleActivePageChange}
          loading={loadingActive}
          totalItems={activeCodesTotal}
          backgroundLoading={backgroundLoading}
        />
      )}
      {tableType === 'disabled' && (
        <PaginationControls
          currentPage={disabledCurrentPage}
          totalPages={disabledTotalPages}
          onPageChange={handleDisabledPageChange}
          loading={loadingDisabled}
          totalItems={disabledCodesTotal}
          backgroundLoading={backgroundLoading}
        />
      )}
      {tableType === 'deleted' && (
        <PaginationControls
          currentPage={deletedCurrentPage}
          totalPages={deletedTotalPages}
          onPageChange={handleDeletedPageChange}
          loading={loadingDeleted}
          totalItems={deletedCodesTotal}
          backgroundLoading={backgroundLoading}
        />
      )}
    </div>
    );
  };

  // Show authentication error if user is not authorized
  if (authError) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>Access Denied</h1>
        </div>
        <div className={styles.errorBanner} style={{ marginTop: '20px' }}>
          {authError}
        </div>
      </div>
    );
  }

  // Show loading state while checking authentication
  if (isCurrenAuthUserLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          Checking authentication...
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>

      <div className={styles.header} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Invite Codes</h1>
        <button 
          onClick={toggleAllCodes}
          className={styles.allCodesButton}
          style={{
            background: 'transparent',
            color: showAllCodes ? '#ff006e' : '#6c7ce7',
            border: `1px solid ${showAllCodes ? '#ff006e' : '#6c7ce7'}`,
            padding: '4px 12px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit'
          }}
          onMouseOver={(e) => {
            e.target.style.background = showAllCodes ? '#ff006e' : '#6c7ce7';
            e.target.style.color = 'white';
          }}
          onMouseOut={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.color = showAllCodes ? '#ff006e' : '#6c7ce7';
          }}
          title={showAllCodes ? "Hide user_feels_send codes" : "Show all codes including user_feels_send"}
        >
          {showAllCodes ? 'Hide User Feels Send' : 'Include Sent Feels'}
        </button>
      </div>


      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button
            onClick={() => setError(null)}
            className={styles.closeError}
          >
            ×
          </button>
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>
          Loading invite codes...
        </div>
      ) : (
        <>
          {renderTable(activeCodes, 'active', 'Active Codes')}
          {renderTable(disabledCodes, 'disabled', 'Disabled Codes')}
          {renderTable(deletedCodes, 'deleted', 'Deleted Codes')}
        </>
      )}

      {/* Date Picker Modal */}
      {showDatePicker && (
        <div className={styles.modalOverlay} onClick={handleCloseDatePicker}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Select New Expiration Date</h3>
              <button
                onClick={handleCloseDatePicker}
                className={styles.modalCloseButton}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div style={{ 
                marginBottom: '20px', 
                padding: '12px 16px', 
                background: 'rgba(255, 0, 110, 0.1)', 
                borderRadius: '8px',
                border: '1px solid rgba(255, 0, 110, 0.3)'
              }}>
                <label className={styles.dateLabel} style={{ marginBottom: '4px', display: 'block' }}>
                  Updating Invite Code:
                </label>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: '700', 
                  color: '#ff006e',
                  fontFamily: 'monospace'
                }}>
                  {datePickerIndex >= 0 && datePickerTable && 
                    (datePickerTable === 'active' ? activeCodes[datePickerIndex]?.inviteCode : disabledCodes[datePickerIndex]?.inviteCode)}
                </div>
              </div>
              <label className={styles.dateLabel}>
                New Expiration Date:
              </label>
              <input
                type="date"
                min={new Date().toISOString().split('T')[0]}
                value={selectedDate && selectedDate !== 'No expiration' ? 
                  (function() {
                    try {
                      // Handle different date formats that might come from the API
                      const date = new Date(selectedDate);
                      if (!isNaN(date.getTime())) {
                        return date.toISOString().split('T')[0];
                      }
                      return '';
                    } catch (e) {
                      return '';
                    }
                  })() : ''}
                onChange={(e) => setSelectedDate(e.target.value)}
                className={styles.dateInput}
              />
            </div>
            <div className={styles.modalFooter}>
              <button
                onClick={() => handleDateSelection(selectedDate)}
                className={`${styles.button} ${styles.saveButton}`}
                disabled={!selectedDate}
              >
                Update Expiration
              </button>
              <button
                onClick={() => {
                  const codes = datePickerTable === 'active' ? activeCodes : disabledCodes;
                  handleClearExpiration(datePickerIndex, datePickerTable);
                  handleCloseDatePicker();
                }}
                className={`${styles.button} ${styles.deleteButton}`}
              >
                Remove Expiration
              </button>
              <button
                onClick={handleCloseDatePicker}
                className={`${styles.button} ${styles.cancelButton}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Code Modal */}
      {showNewCodeModal && (
        <div className={styles.modalOverlay} onClick={handleNewCodeCancel}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Create New Invite Code</h3>
              <button
                onClick={handleNewCodeCancel}
                className={styles.modalCloseButton}
              >
                ×
              </button>
            </div>
            {modalError && (
              <div style={{
                backgroundColor: '#dc3545',
                color: 'white',
                padding: '12px 16px',
                margin: '0 28px',
                borderRadius: '6px',
                fontSize: '14px'
              }}>
                {modalError}
              </div>
            )}
            <div className={styles.modalBody}>
              <div style={{ marginBottom: '20px' }}>
                <label className={styles.dateLabel}>
                  Invite Code ID (Optional):
                </label>
                <input
                  type="text"
                  value={newCodeData.inviteCode}
                  onChange={(e) => handleNewCodeFieldChange('inviteCode', e.target.value)}
                  className={styles.inputField}
                  placeholder="Leave blank for auto-generated code"
                  maxLength={25}
                  style={{ marginBottom: '10px' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <small style={{ color: '#999', fontSize: '12px' }}>
                    Alphanumeric only (A-Z, 0-9), max 25 characters
                  </small>
                  <small style={{ color: '#666', fontSize: '11px', fontFamily: 'monospace' }}>
                    {newCodeData.inviteCode.length}/25
                  </small>
                </div>
                {!newCodeData.inviteCode && suggestedCode && (
                  <div style={{ marginTop: '5px' }}>
                    <small style={{ color: '#666', fontSize: '11px', fontStyle: 'italic' }}>
                      Auto-generated: <span style={{ fontFamily: 'monospace', color: '#999' }}>{suggestedCode}</span>
                    </small>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label className={styles.dateLabel}>
                  Invite Limit (Required):
                </label>
                <input
                  type="number"
                  min="1"
                  value={newCodeData.inviteLimit}
                  onChange={(e) => handleNewCodeFieldChange('inviteLimit', e.target.value)}
                  className={styles.inputField}
                  placeholder="Enter maximum number of signups"
                  style={{ marginBottom: '10px' }}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label className={styles.dateLabel}>
                  Expiration Date (Optional):
                </label>
                <input
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                  value={newCodeData.expirationDate}
                  onChange={(e) => handleNewCodeFieldChange('expirationDate', e.target.value)}
                  className={styles.dateInput}
                  style={{ marginBottom: '10px' }}
                />
                <small style={{ color: '#999', fontSize: '12px', display: 'block' }}>
                  Leave blank for no expiration
                </small>
              </div>

              {/* Geo-restriction section */}
              <div style={{ marginBottom: '20px' }}>
                <label className={styles.dateLabel} style={{ marginBottom: '10px', display: 'block' }}>
                  WHERE CAN THIS CODE BE REDEEMED?
                </label>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                  <label className={styles.radioContainer}>
                    <input
                      type="radio"
                      name="geoRestriction"
                      value="selected_countries"
                      checked={newCodeData.geoRestriction === 'selected_countries'}
                      onChange={(e) => handleNewCodeFieldChange('geoRestriction', e.target.value)}
                    />
                    <span className={styles.radioLabel}>Allow Listed Countries</span>
                  </label>
                  <label className={styles.radioContainer}>
                    <input
                      type="radio"
                      name="geoRestriction"
                      value="worldwide"
                      checked={newCodeData.geoRestriction === 'worldwide'}
                      onChange={(e) => handleNewCodeFieldChange('geoRestriction', e.target.value)}
                    />
                    <span className={styles.radioLabel}>Worldwide</span>
                  </label>
                </div>
              </div>

              {/* Advanced Options Toggle */}
              <div 
                style={{ 
                  marginBottom: '20px', 
                  cursor: 'pointer', 
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #555',
                  backgroundColor: showAdvancedOptions ? '#333' : 'transparent'
                }}
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: '#999', fontSize: '14px' }}>Advanced Options</span>
                  <span style={{ color: '#999', fontSize: '18px' }}>
                    {showAdvancedOptions ? '−' : '+'}
                  </span>
                </div>
              </div>

              {/* Advanced Options Content */}
              {showAdvancedOptions && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label className={styles.dateLabel}>
                      Allow Multiple Signups:
                    </label>
                    <select
                      value={newCodeData.allowMultipleSignup}
                      onChange={(e) => handleNewCodeFieldChange('allowMultipleSignup', e.target.value === 'true')}
                      className={styles.selectField}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <label className={styles.dateLabel}>
                      Type:
                    </label>
                    <select
                      value={newCodeData.referralGroupObject}
                      onChange={(e) => handleNewCodeFieldChange('referralGroupObject', e.target.value)}
                      className={styles.selectField}
                    >
                      <option value="user_feels_send">user_feels_send</option>
                      <option value="artist">artist</option>
                      <option value="song">song</option>
                      <option value="feels">feels</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label className={styles.dateLabel}>
                      FeelsID (F_ID) (Optional):
                    </label>
                    <input
                      type="text"
                      value={newCodeData.feelsId}
                      onChange={(e) => handleNewCodeFieldChange('feelsId', e.target.value)}
                      className={styles.inputField}
                      placeholder="Enter Feels ID for FAST FEELS link"
                    />
                    <small style={{ color: '#999', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                      This will create the FAST FEELS URL for the admin interface
                    </small>
                  </div>
                </>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                onClick={handleNewCodeSave}
                className={`${styles.button} ${styles.saveButton}`}
                disabled={!newCodeData.inviteLimit || parseInt(newCodeData.inviteLimit) < 1 || isSavingSong}
                style={{
                  opacity: (!newCodeData.inviteLimit || parseInt(newCodeData.inviteLimit) < 1 || isSavingSong) ? 0.5 : 1,
                  cursor: (!newCodeData.inviteLimit || parseInt(newCodeData.inviteLimit) < 1 || isSavingSong) ? 'not-allowed' : 'pointer'
                }}
              >
                {isSavingSong ? 'Creating...' : 'Create Code'}
              </button>
              <button
                onClick={handleNewCodeCancel}
                className={`${styles.button} ${styles.cancelButton}`}
                disabled={isSavingSong}
                style={{
                  opacity: isSavingSong ? 0.5 : 1,
                  cursor: isSavingSong ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Limit Modal */}
      {showInviteLimitModal && (
        <div className={styles.modalOverlay} onClick={handleInviteLimitCancel}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Edit Invite Limit</h3>
              <button
                onClick={handleInviteLimitCancel}
                className={styles.modalCloseButton}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div style={{ 
                marginBottom: '20px', 
                padding: '12px 16px', 
                background: 'rgba(255, 0, 110, 0.1)', 
                borderRadius: '8px',
                border: '1px solid rgba(255, 0, 110, 0.3)'
              }}>
                <label className={styles.dateLabel} style={{ marginBottom: '4px', display: 'block' }}>
                  Updating Invite Code:
                </label>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: '700', 
                  color: '#ff006e',
                  fontFamily: 'monospace',
                  marginBottom: '8px'
                }}>
                  {inviteLimitModalData.table && inviteLimitModalData.index >= 0 &&
                    (inviteLimitModalData.table === 'active' 
                      ? activeCodes[inviteLimitModalData.index]?.inviteCode 
                      : disabledCodes[inviteLimitModalData.index]?.inviteCode)}
                </div>
                <div style={{ color: '#b8b8b8', fontSize: '14px' }}>
                  Current Limit: {inviteLimitModalData.currentLimit}
                </div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label className={styles.dateLabel}>
                  New Invite Limit:
                </label>
                <input
                  type="number"
                  min="1"
                  value={inviteLimitModalData.newLimit}
                  onChange={(e) => setInviteLimitModalData(prev => ({
                    ...prev,
                    newLimit: e.target.value
                  }))}
                  className={styles.inputField}
                  placeholder="Enter new maximum number of signups"
                  style={{ marginBottom: '10px' }}
                />
                <small style={{ color: '#999', fontSize: '12px', display: 'block' }}>
                  Must be a number greater than 0
                </small>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                onClick={handleInviteLimitSave}
                className={`${styles.button} ${styles.saveButton}`}
              >
                Update Limit
              </button>
              <button
                onClick={handleInviteLimitCancel}
                className={`${styles.button} ${styles.cancelButton}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Expire Confirmation Modal */}
      {showQuickExpireModal && (
        <div className={styles.modalOverlay} onClick={handleQuickExpireCancel}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Quick Expire Confirmation</h3>
              <button
                onClick={handleQuickExpireCancel}
                className={styles.modalCloseButton}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p style={{ color: '#ffffff', fontSize: '16px', lineHeight: '1.5', marginBottom: '20px' }}>
                Are you sure? If you confirm, this invite code will expire immediately.
              </p>
              <div style={{ 
                background: 'rgba(255, 255, 255, 0.05)', 
                padding: '12px', 
                borderRadius: '6px', 
                marginBottom: '20px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <strong style={{ color: '#ff006e' }}>Invite Code:</strong> {quickExpireModalData.inviteCode}
              </div>
              <p style={{ color: '#999', fontSize: '14px' }}>
                This action will set the expiration to 20 seconds from now and mark the code as expired, making it inactive immediately.
              </p>
            </div>
            <div className={styles.modalFooter}>
              <button
                onClick={handleQuickExpireConfirm}
                className={`${styles.button} ${styles.deleteButton}`}
              >
                Confirm - Expire Now
              </button>
              <button
                onClick={handleQuickExpireCancel}
                className={`${styles.button} ${styles.cancelButton}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Access Modal */}
      {showManageAccessModal && (
        <div className={styles.modalOverlay} onClick={handleCloseManageAccess}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Manage Access</h3>
              <button
                onClick={handleCloseManageAccess}
                className={styles.modalCloseButton}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div style={{ marginBottom: '24px' }}>
                <label className={styles.dateLabel} style={{ marginBottom: '12px', display: 'block' }}>
                  Authorized Users
                </label>
                <div style={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '8px'
                }}>
                  {allowlistUsers.length === 0 ? (
                    <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                      No users in allowlist
                    </p>
                  ) : (
                    allowlistUsers.map((alias, index) => (
                      <div 
                        key={index} 
                        style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          marginBottom: '4px',
                          background: 'rgba(255, 255, 255, 0.05)'
                        }}
                      >
                        <span style={{ 
                          color: '#ff006e', 
                          fontWeight: '600',
                          fontFamily: 'monospace',
                          fontSize: '14px'
                        }}>
                          {alias}@musicmakesusfeels.com
                        </span>
                        <button
                          onClick={() => handleRemoveUser(alias)}
                          className={`${styles.button} ${styles.deleteButton}`}
                          style={{
                            padding: '4px 12px',
                            fontSize: '12px',
                            marginLeft: '12px'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div>
                <label className={styles.dateLabel} style={{ marginBottom: '8px', display: 'block' }}>
                  Add New User
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={newUserAlias}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Prevent typing @ symbol
                      if (!value.includes('@')) {
                        setNewUserAlias(value);
                      }
                    }}
                    className={styles.inputField}
                    placeholder="Enter username (e.g., john)"
                    onKeyPress={(e) => {
                      // Block @ symbol from being typed
                      if (e.key === '@') {
                        e.preventDefault();
                        return;
                      }
                      if (e.key === 'Enter') handleAddUser();
                    }}
                    style={{ flex: 1 }}
                  />
                  <button
                    onClick={handleAddUser}
                    className={`${styles.button} ${styles.saveButton}`}
                    disabled={!newUserAlias.trim()}
                  >
                    Add User
                  </button>
                </div>
                <small style={{ color: '#999', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                  Enter only the username part (before @ symbol). Domain will be added automatically.
                </small>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                onClick={handleCloseManageAccess}
                className={`${styles.button} ${styles.cancelButton}`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className={styles.modalOverlay} onClick={() => setShowBulkDeleteConfirm(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Confirm Bulk Delete</h3>
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className={styles.modalCloseButton}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '16px', marginBottom: '12px' }}>
                  Are you sure you want to permanently delete {selectedCodesForDelete.size} selected invite codes?
                </div>
                <div style={{ fontSize: '14px', color: '#ff6b6b', marginBottom: '16px' }}>
                  This action cannot be undone. The codes will be marked as deleted.
                </div>
                <div style={{ 
                  background: '#1a1a1a', 
                  border: '1px solid #333', 
                  borderRadius: '6px', 
                  padding: '12px',
                  marginTop: '16px'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                    Selected Codes:
                  </div>
                  <div style={{ 
                    maxHeight: '150px', 
                    overflowY: 'auto',
                    fontSize: '12px',
                    color: '#ccc'
                  }}>
                    {Array.from(selectedCodesForDelete).map(inviteCode => (
                      <div key={inviteCode} style={{ marginBottom: '4px' }}>
                        • {inviteCode}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className={`${styles.button} ${styles.cancelButton}`}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                className={`${styles.button}`}
                style={{
                  backgroundColor: '#ff006e',
                  color: 'white',
                  fontWeight: '600'
                }}
              >
                Delete {selectedCodesForDelete.size} Codes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Geo Restriction Modal */}
      {showGeoRestrictionModal && (
        <div className={styles.modalOverlay} onClick={handleGeoRestrictionModalCancel}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Edit Geo Restriction</h3>
              <button
                onClick={handleGeoRestrictionModalCancel}
                className={styles.modalCloseButton}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div style={{ 
                marginBottom: '20px', 
                padding: '12px 16px', 
                background: 'rgba(255, 0, 110, 0.1)', 
                borderRadius: '8px',
                border: '1px solid rgba(255, 0, 110, 0.3)'
              }}>
                <label className={styles.dateLabel} style={{ marginBottom: '4px', display: 'block' }}>
                  Updating Invite Code:
                </label>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: '700', 
                  color: '#ff006e',
                  fontFamily: 'monospace',
                  marginBottom: '8px'
                }}>
                  {geoRestrictionModalData.table && geoRestrictionModalData.index >= 0 &&
                    (geoRestrictionModalData.table === 'active' 
                      ? activeCodes[geoRestrictionModalData.index]?.inviteCode 
                      : disabledCodes[geoRestrictionModalData.index]?.inviteCode)}
                </div>
                <div style={{ color: '#b8b8b8', fontSize: '14px' }}>
                  Current Setting: {geoRestrictionModalData.currentGeoRestriction === 'worldwide' ? 'Worldwide' : 'Allow Listed Countries'}
                </div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label className={styles.dateLabel} style={{ marginBottom: '12px', display: 'block' }}>
                  Choose geo restriction setting:
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label className={styles.radioContainer}>
                    <input
                      type="radio"
                      name="geoRestriction"
                      value="worldwide"
                      checked={geoRestrictionModalData.newGeoRestriction === 'worldwide'}
                      onChange={(e) => setGeoRestrictionModalData(prev => ({
                        ...prev,
                        newGeoRestriction: e.target.value
                      }))}
                    />
                    <span className={styles.radioLabel}>Worldwide</span>
                  </label>
                  <label className={styles.radioContainer}>
                    <input
                      type="radio"
                      name="geoRestriction"
                      value="selected_countries"
                      checked={geoRestrictionModalData.newGeoRestriction === 'selected_countries'}
                      onChange={(e) => setGeoRestrictionModalData(prev => ({
                        ...prev,
                        newGeoRestriction: e.target.value
                      }))}
                    />
                    <span className={styles.radioLabel}>Allow Listed Countries</span>
                  </label>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                onClick={handleGeoRestrictionModalSubmit}
                className={`${styles.button} ${styles.saveButton}`}
              >
                Update Setting
              </button>
              <button
                onClick={handleGeoRestrictionModalCancel}
                className={`${styles.button} ${styles.cancelButton}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CodeManager;
